import { Locale } from "../Locale.ts";

/**
 * UI strings for the flash-card deck, the printable cards panel, and the
 * quiz pages (`src/FlashCards/**`) — ADR 0051 part 2.
 *
 * A few templates reorder their placeholders between locales on purpose
 * (`flashCards.cappedByPoolNote`, `answeredCountsTemplate`) - `format()`
 * fills named `{tokens}` wherever they appear, so Spanish and French can put
 * a noun before its qualifier the way each language actually does, without
 * the call site knowing or caring.
 */
const en = {
    "flashCards.ariaFlippedTemplate": "{front} equals {back}. Tap to hide the answer.",
    "flashCards.ariaUnflippedTemplate": "{front}. Tap to show the answer.",
    "flashCards.tapToFlip": "Tap to flip",
    "flashCards.emptyDeck": "Pick an operation and at least one number to start a deck.",
    "flashCards.cardProgress": "Card {current} of {total}",
    "flashCards.previous": "Previous",
    "flashCards.next": "Next",
    "flashCards.newDeck": "New deck",
    "flashCards.swipeHint": "Swipe or use the arrow keys to move between cards, space to flip.",
    "flashCards.changeWhatToPractice": "Change what to practice",
    "flashCards.frequentlyAskedQuestions": "Frequently asked questions",
    "flashCards.printTheseCards": "Print these flash cards",
    "flashCards.cards": "cards",
    "flashCards.printIntroPrefix":
        "{count} cards to a sheet, sized like an index card. Cut along the dashed lines. ",
    "flashCards.printIntroDoubleSided":
        "Print double-sided, flipping on the long edge, and each answer lands on the back of its own card.",
    "flashCards.printIntroSingleSided":
        "Answer backs are turned off, so the cards print problem-side only.",
    "flashCards.sheetsOfCardsLabel": "Sheets of cards: ",
    "flashCards.addSheetAria": "Add a sheet of cards",
    "flashCards.removeSheetAria": "Remove a sheet of cards",
    "flashCards.answersOnBackLabel": "Answers on the back: ",
    "flashCards.withAnswerBacks": ", with answer backs",
    "flashCards.onSheetsSuffix": " on {n} {sheetWord}",
    "flashCards.sheet": "sheet",
    "flashCards.sheets": "sheets",
    "flashCards.cappedByPoolNote":
        "Every card is different, and these settings only make {count} different " +
        "{operation} {factWord} — that is the whole set. Widen the number range for more.",
    "flashCards.fact": "fact",
    "flashCards.facts": "facts",
    "flashCards.chooseOperationNote":
        "Choose an operation and at least one number to print a set of cards.",
    "flashCards.loadingPreview": "Loading preview…",
    "flashCards.openFlashCardsPdf": "Open the flash cards PDF",
    "flashCards.buildingCards": "Building your cards…",
    "flashCards.previewOfFlashCardsPage1": "Preview of flash cards page 1",
    "flashCards.missedFactsCountTemplate": "{count} {factWord} worth another look.",
    "flashCards.printForPractice": "Print these for practice",
    "flashCards.loadingPracticeSheet": "Loading practice sheet…",
    "flashCards.quizEmptyNote":
        "Choose an operation and at least one number below to start a quiz.",
    "flashCards.chooseDifferentLength": "Choose a different length",
    "flashCards.howLongDoYouWant": "How long do you want?",
    "flashCards.noClockJustPractise": "No clock — just practise",
    "flashCards.changeWhatToPractise": "Change what to practise",
    "flashCards.thirtySeconds": "30 seconds",
    "flashCards.oneMinute": "1 minute",
    "flashCards.twoMinutes": "2 minutes",
    "flashCards.threeMinutes": "3 minutes",
    "flashCards.timeLeftAria": "Time left",
    "flashCards.scoreTemplate": "Score {n}",
    "flashCards.streakTemplate": "Streak {n}",
    "flashCards.getReady": "Get ready…",
    "flashCards.answerForAriaTemplate": "Answer for {front}",
    "flashCards.enterButton": "Enter",
    "flashCards.correct": "Correct",
    "flashCards.skipped": "Skipped",
    "flashCards.notQuite": "Not quite",
    "flashCards.finishButton": "Finish",
    "flashCards.quizHint":
        "Type the answer and press Enter. Press Enter with the box empty to skip.",
    "flashCards.noClockHintSuffix": " There is no clock — stop whenever you like.",
    "flashCards.correctCaption": "correct",
    "flashCards.againstTheClockSuffix": " against the clock",
    "flashCards.answeredCountsTemplate": "{answered} answered · {wrong} wrong · {skipped} skipped",
    "flashCards.newBest": "New best!",
    "flashCards.yourBestTemplate": "Your best: {n}",
    "flashCards.worthAnotherLookHeading": "Worth another look",
    "flashCards.skippedLower": "skipped",
    "flashCards.youPutTemplate": "you put {typed}",
    "flashCards.playAgain": "Play again",
    "flashCards.gameEmptyNote":
        "Choose an operation and at least one number below to start the game.",
    "flashCards.gameHowToPlay":
        "Facts fall from the top. Type the answer and press Enter to clear one before it lands. Three land and the game is over.",
    "flashCards.gameStart": "Start the game",
    "flashCards.gameCalmerPrefix": "Rather practise without the rush? Try the ",
    "flashCards.gameCalmerLink": "quiz with no clock",
    "flashCards.gameCalmerSuffix": ".",
    "flashCards.gameLevelTemplate": "Level {n}",
    "flashCards.gameLivesAriaTemplate": "{n} lives left",
    "flashCards.gameOneLifeAria": "1 life left",
    "flashCards.gameLastLife": "A fact landed. One life left.",
    "flashCards.gamePause": "Pause",
    "flashCards.gameResume": "Resume",
    "flashCards.gamePaused": "Paused",
    "flashCards.gamePausedHint": "The facts are hidden until you resume.",
    "flashCards.gameAnswerAria": "Type an answer to clear a falling fact",
    "flashCards.gameHint": "Type the answer and press Enter. A wrong guess only costs time.",
    "flashCards.gameLifeLostTemplate": "A fact landed. {n} lives left.",
    "flashCards.gameLevelUpTemplate": "Level {n}. They fall faster now.",
    "flashCards.gameOver": "Game over",
    "flashCards.gameClearedCaption": "facts cleared",
    "flashCards.gameReachedTemplate": "Reached level {level} · {wrong} wrong guesses",
    "flashCards.dropChangeFacts": "Choose different facts",
    "flashCards.chartIntro": "Answer a fact to fill in its square, and its mirror across the diagonal. Tap any empty square to choose your own. Your chart is saved on this device.",
    "flashCards.chartProgressTemplate": "{filled} of {total} facts",
    "flashCards.chartHint": "Type the answer and press Enter. Enter on an empty box skips to another square.",
    "flashCards.chartStartOver": "Start a new chart",
    "flashCards.chartConfirmStartOver": "Clear your whole chart?",
    "flashCards.chartConfirmYes": "Yes, clear it",
    "flashCards.chartConfirmNo": "Keep it",
    "flashCards.chartComplete": "You filled the whole chart!",
    "flashCards.chartCompleteDetailTemplate": "All {total} facts · {wrong} wrong answers along the way",
    "flashCards.chartGridCaption": "Your multiplication chart",
    "flashCards.chartCellEmptyAriaTemplate": "{a} times {b}: empty. Ask this one.",
    "flashCards.chartCellTrickyAriaTemplate": "{a} times {b}: tricky. Ask this one.",
    "flashCards.chartCellFilledAriaTemplate": "{a} times {b}: {answer}",
    "flashCards.chartWhichChart": "Which chart?",
    "flashCards.chartPickAddition": "Addition",
    "flashCards.chartPickMultiplication": "Multiplication",
    "flashCards.chartGridCaptionAddition": "Your addition chart",
    "flashCards.chartCellEmptyAdditionAriaTemplate": "{a} plus {b}: empty. Ask this one.",
    "flashCards.chartCellTrickyAdditionAriaTemplate": "{a} plus {b}: tricky. Ask this one.",
    "flashCards.chartCellFilledAdditionAriaTemplate": "{a} plus {b}: {answer}",
    "flashCards.productModePrompt": "Who are you playing?",
    "flashCards.productVsComputer": "Play the computer",
    "flashCards.productTwoPlayers": "Two players, one screen",
    "flashCards.productHowTo": "Move one clip each turn, then type the product of the two clips to claim that square. Get four in a row to win.",
    "flashCards.productYourTurn": "Your turn",
    "flashCards.productComputerThinking": "The computer is thinking…",
    "flashCards.productPlayerTurnTemplate": "Player {n}'s turn",
    "flashCards.productPlaceFirstClip": "Put clip A on any factor. Nothing is claimed on the first move.",
    "flashCards.productPlaceSecondClip": "Put clip B on a factor, then type the product.",
    "flashCards.productMoveOneClip": "Move clip A or clip B to a new factor.",
    "flashCards.productTypeProductTemplate": "What is {front}? Type the answer to claim the square.",
    "flashCards.productClipLabelTemplate": "Clip {name}",
    "flashCards.productMoveClipAriaTemplate": "Move clip {name} to {n}",
    "flashCards.productComputerMovedTemplate": "The computer moved clip {name} to {n} and took {product}.",
    "flashCards.productYouWin": "Four in a row: you win!",
    "flashCards.productComputerWins": "The computer got four in a row.",
    "flashCards.productPlayerWinsTemplate": "Player {n} wins with four in a row!",
    "flashCards.productDraw": "No moves left: it's a draw.",
    "flashCards.productChangeMode": "Change who you are playing",
    "flashCards.productBoardCaption": "Product Game board",
    "flashCards.productCellFreeTemplate": "{product}: free",
    "flashCards.productCellYoursTemplate": "{product}: yours",
    "flashCards.productCellComputerTemplate": "{product}: the computer's",
    "flashCards.productCellPlayerTemplate": "{product}: player {n}'s",
    "flashCards.productTurnPasses": "The turn passes.",
    "flashCards.productVariantPrompt": "Which game do you want to play?",
    "flashCards.productVariantProduct": "Product Game (×)",
    "flashCards.productVariantSum": "Sum Game (+)",
    "flashCards.productSumHowTo": "Move one clip each turn, then type the sum of the two clips to claim a square showing that sum. If more than one is free, you choose which. Get four in a row to win.",
    "flashCards.productSumPlaceFirstClip": "Put clip A on any number. Nothing is claimed on the first move.",
    "flashCards.productSumPlaceSecondClip": "Put clip B on a number, then type the sum.",
    "flashCards.productSumMoveOneClip": "Move clip A or clip B to a new number.",
    "flashCards.productSumTypeTemplate": "What is {front}? Type the answer to claim a square.",
    "flashCards.productChooseSquare": "Choose which square to claim",
    "flashCards.productSumComputerMovedTemplate": "The computer moved clip {name} to {n} and took a square showing {product}.",
    "flashCards.productSumBoardCaption": "Sum Game board",
    "flashCards.productCellChooseTemplate": "{product}: claim this square",
    "flashCards.munchModePrompt": "How do you want to play?",
    "flashCards.munchWithMonsters": "With monsters",
    "flashCards.munchCalm": "Calm: no monsters",
    "flashCards.munchHowTo": "Move with the arrow keys, or tap a square next to you. Press Space, or tap yourself, to munch. Eat every square that fits the rule; a wrong munch costs a life.",
    "flashCards.munchRuleMultiples": "Multiples of {n}",
    "flashCards.munchRuleProducts": "Makes {n}",
    "flashCards.munchRuleFactors": "Factors of {n}",
    "flashCards.munchLeftTemplate": "{n} left to eat",
    "flashCards.munchLevelDone": "Level complete!",
    "flashCards.munchWhyMultipleTemplate": "{v} is not a multiple of {n}: {n} × {k} = {lo} and {n} × {k1} = {hi}.",
    "flashCards.munchWhyMultipleSmallTemplate": "{v} is not a multiple of {n}: the smallest multiple of {n} is {n}.",
    "flashCards.munchWhyFactorTemplate": "{v} is not a factor of {n}: {n} ÷ {v} = {q} remainder {r}.",
    "flashCards.munchWhyProductTemplate": "{front} = {p}, not {n}.",
    "flashCards.munchLostLife": "You lose a life.",
    "flashCards.munchCaught": "A monster caught you! You lose a life.",
    "flashCards.munchYumTemplate": "Yum! {text} fits.",
    "flashCards.munchBoardLabel": "Game board. Use the arrow keys to move and Space to munch.",
    "flashCards.munchSquareEaten": "eaten",
    "flashCards.munchSquareYou": "you are here",
    "flashCards.munchSquareMonster": "monster here",
    "flashCards.munchUp": "Up",
    "flashCards.munchDown": "Down",
    "flashCards.munchLeft": "Left",
    "flashCards.munchRight": "Right",
    "flashCards.munchMunchButton": "Munch",
    "flashCards.munchScoreCaption": "squares munched",
    "flashCards.munchReachedTemplate": "Reached level {level} · {wrong} wrong munches",
    "flashCards.munchChangeMode": "Change how you play",
    "flashCards.munchWhichFacts": "Which facts do you want to practise?",
    "flashCards.munchWhyAddTemplate": "{front} makes {p}, not {n}.",
    "flashCards.munchWhySubtractTemplate": "{front} leaves {p}, not {n}.",
    "flashCards.munchWhyDivideTemplate": "{front} gives {p}, not {n}.",
    "flashCards.collieHowTo": "Help the border collie cross the floor. Only tiles that fit the rule are strong enough to hold her; the rest break and she falls back to the start. Move with the arrow keys, or tap a tile next to her.",
    "flashCards.collieStart": "Start crossing",
    "flashCards.collieFloorTemplate": "Floor {n}",
    "flashCards.collieFallsTemplate": "Falls: {n}",
    "flashCards.collieFellTemplate": "Crack! {why} Back to the start.",
    "flashCards.collieSafeTemplate": "{text} holds.",
    "flashCards.collieCrossed": "She made it across!",
    "flashCards.collieCrossedClean": "Across without a single fall.",
    "flashCards.collieCrossedOneFall": "Across with one fall.",
    "flashCards.collieCrossedFallsTemplate": "Across with {n} falls.",
    "flashCards.collieNextFloor": "Next floor",
    "flashCards.collieOtherSide": "The other side",
    "flashCards.collieStartLedge": "Start",
    "flashCards.collieBoardLabel": "Tile floor. Use the arrow keys to move the dog across.",
    "flashCards.collieTileBroken": "broken",
    "flashCards.collieTileSafe": "safe",
    "flashCards.collieDogHere": "the dog is here",
    "flashCards.collieStarsAriaTemplate": "{n} of 3 stars",
    "flashCards.collieWhichFacts": "Which facts do you want to practise?",
    // Math Facts Soccer (ADR 0059).
    "flashCards.soccerHowToPlay": "Each penalty is a fact. Type the answer and press Enter to shoot. Be quick: once the keeper is set, a right answer gets saved.",
    "flashCards.soccerWhichFacts": "Which facts?",
    "flashCards.soccerChooseKeeper": "Choose your keeper to kick off",
    "flashCards.soccerKeeperRookie": "Rookie",
    "flashCards.soccerKeeperPro": "Pro",
    "flashCards.soccerKeeperLegend": "Legend",
    "flashCards.soccerKeeperSecondsTemplate": "{n} seconds",
    "flashCards.soccerGoalsTemplate": "Goals {n}",
    "flashCards.soccerKickTemplate": "Kick {n} of {total}",
    "flashCards.soccerRecordAriaTemplate": "{goals} goals from {kicks} kicks",
    "flashCards.soccerKeeperGettingReady": "Keeper getting set",
    "flashCards.soccerKeeperSetShort": "Keeper is set!",
    "flashCards.soccerKeeperSet": "The keeper is set. A right answer now will be saved.",
    "flashCards.soccerAnswerAria": "Type the answer to take the penalty",
    "flashCards.soccerShoot": "Shoot",
    "flashCards.soccerHint": "Type the answer and press Enter to shoot. Press Enter on an empty box to skip.",
    "flashCards.soccerBannerGoal": "GOAL!",
    "flashCards.soccerBannerHatTrick": "HAT TRICK!",
    "flashCards.soccerBannerSaved": "SAVED!",
    "flashCards.soccerBannerWide": "WIDE!",
    "flashCards.soccerBannerSkipped": "Skipped",
    "flashCards.soccerVerdictGoal": "Goal!",
    "flashCards.soccerVerdictSaved": "Right, but the keeper was set",
    "flashCards.soccerVerdictWide": "Wide",
    "flashCards.soccerVerdictSkipped": "Skipped",
    "flashCards.soccerFullTime": "Full time",
    "flashCards.soccerGoalsFromTemplate": "goals from {total} penalties",
    "flashCards.soccerCountsTemplate": "{saved} saved · {wide} wide · {skipped} skipped",
    "flashCards.soccerSlowHeading": "You knew these. Now get them quicker",
    "flashCards.soccerChangeKeeper": "Choose a different keeper",
    "flashCards.footballHowToPlay": "Each play is a fact. Four receivers run their routes, each wearing a number. Tap the one wearing the answer, or press 1 to 4, to throw. Throw before the pass rush arrives or it is a sack. A game is four drives.",
    "flashCards.footballWhichFacts": "Which facts?",
    "flashCards.footballChooseDefense": "Choose a defense to kick off",
    "flashCards.footballDefenseRookie": "Rookie",
    "flashCards.footballDefensePro": "Pro",
    "flashCards.footballDefenseAllPro": "All-Pro",
    "flashCards.footballDefenseSecondsTemplate": "{n} seconds",
    "flashCards.footballPointsTemplate": "Points {n}",
    "flashCards.footballDriveTemplate": "Drive {n} of {total}",
    "flashCards.footballDownAndDistanceTemplate": "{down} & {distance}",
    "flashCards.footballDownFirst": "1st",
    "flashCards.footballDownSecond": "2nd",
    "flashCards.footballDownThird": "3rd",
    "flashCards.footballDownFourth": "4th",
    "flashCards.footballGoal": "goal",
    "flashCards.footballBallOnTemplate": "Ball on {spot}",
    "flashCards.footballOwnTemplate": "Own {n}",
    "flashCards.footballOppTemplate": "Opp {n}",
    "flashCards.footballFieldAria": "The field. Four receivers run upfield. Throw to the one wearing the answer.",
    "flashCards.footballReceiverAriaTemplate": "Receiver {n}: {value}",
    "flashCards.footballReceiverHiddenAriaTemplate": "Receiver {n}",
    "flashCards.footballQuarterback": "QB",
    "flashCards.footballEndZone": "END ZONE",
    "flashCards.footballRush": "Pass rush",
    "flashCards.footballNextPlay": "Next play",
    "flashCards.footballHint": "Tap a receiver or press 1 to 4 to throw. Throw before the rush arrives.",
    "flashCards.footballCaughtTemplate": "Caught for {n} yards.",
    "flashCards.footballCaughtOne": "Caught for 1 yard.",
    "flashCards.footballFirstDown": "First down.",
    "flashCards.footballTouchdown": "Touchdown!",
    "flashCards.footballIncompleteTemplate": "Incomplete. {front} = {answer}.",
    "flashCards.footballSackedTemplate": "Sacked for a loss of {n} yards. {front} = {answer}.",
    "flashCards.footballSackedOneTemplate": "Sacked for a loss of 1 yard. {front} = {answer}.",
    "flashCards.footballSackedNoLossTemplate": "Sacked. {front} = {answer}.",
    "flashCards.footballTurnover": "Turnover on downs.",
    "flashCards.footballFinal": "Final whistle",
    "flashCards.footballPointsCaption": "points",
    "flashCards.footballCountsTemplate": "Touchdowns {touchdowns} · Passes caught {completions} of {attempts} · Sacks {sacks}",
    "flashCards.footballYouThrewTemplate": "you threw to {n}",
    "flashCards.footballSackedLower": "sacked",
    "flashCards.footballChangeDefense": "Change the defense or the facts",
    // Math Runner (ADR 0124).
    "flashCards.runnerHowToPlay": "Each row of gates is a fact. Three lanes run toward you, and every gate wears a number. Only one is the answer. Move to its lane with the arrow keys, the keys 1 to 3, a tap or a swipe, and pick it before the row reaches the runner. You have three hearts. The runner speeds up as you pass gates and slows down when you stumble.",
    "flashCards.runnerWhichFacts": "Which facts?",
    "flashCards.runnerChoosePace": "Choose a pace to start",
    "flashCards.runnerPaceJog": "Jog",
    "flashCards.runnerPaceRun": "Run",
    "flashCards.runnerPaceSprint": "Sprint",
    "flashCards.runnerPaceSecondsTemplate": "{n} seconds to start",
    "flashCards.runnerScoreTemplate": "Score {n}",
    "flashCards.runnerHeartsAriaTemplate": "Hearts left: {n}",
    "flashCards.runnerTrackAria": "The track. Three lanes of gates run toward the runner. Pick the lane with the answer.",
    "flashCards.runnerLaneAriaTemplate": "Lane {n}: {value}",
    "flashCards.runnerLaneHiddenAriaTemplate": "Lane {n}",
    "flashCards.runnerAnnounceTemplate": "{front}. Lane 1: {a}. Lane 2: {b}. Lane 3: {c}.",
    "flashCards.runnerHint": "Tap or swipe to a lane, or use the arrow keys, 1 to 3 or Space. Pick a lane for every row: a row with no pick is a stumble.",
    "flashCards.runnerPassed": "Through the gate!",
    "flashCards.runnerStumbledTemplate": "Stumbled. {front} = {answer}.",
    "flashCards.runnerNoPickTemplate": "Stumbled: no lane picked. {front} = {answer}.",
    "flashCards.runnerFinal": "Run over",
    "flashCards.runnerScoreCaption": "gates passed",
    "flashCards.runnerCountsTemplate": "Wrong gates {wrong} · No lane picked {late}",
    "flashCards.runnerYouPickedTemplate": "you picked {n}",
    "flashCards.runnerNoPickLower": "no lane picked",
    "flashCards.runnerChangePace": "Change the pace or the facts",
    // Factor Merge (ADR 0128).
    "flashCards.mergeHowToPlay": "Each card is a target. Tap a tile, then tap another to multiply them: the second tile becomes the product. A merge only works if the product is a factor of one of the targets. When a tile equals a target, the target is made and a new one is dealt. New tiles keep arriving, and the run ends when no merge is left. Three times a run you can clear a tile.",
    "flashCards.mergeLevelsLabel": "Level",
    "flashCards.mergeLevelG3": "Grade 3",
    "flashCards.mergeLevelG4": "Grade 4",
    "flashCards.mergeLevelG5": "Grade 5",
    "flashCards.mergeLevelG6": "Grade 6",
    "flashCards.mergeLevelG7": "Grade 7",
    "flashCards.mergeLevelG8": "Grade 8",
    "flashCards.mergeBoardAria": "The board. Tap a tile, then another tile to merge them. The arrow keys move between tiles, and Space or Enter picks one.",
    "flashCards.mergeTargetsLabel": "Targets",
    "flashCards.mergeTargetMake": "Make {n}",
    "flashCards.mergeTargetLcm": "LCM of {a} and {b}",
    "flashCards.mergeTargetLcm3": "LCM of {a}, {b} and {c}",
    // Grade 8 cards and lines as read aloud, built from the factors (the visible text uses superscripts).
    "flashCards.mergeSpokenPower": "{base} to the power {exponent}",
    "flashCards.mergeSpokenSquared": "{base} squared",
    "flashCards.mergeSpokenCubed": "{base} cubed",
    "flashCards.mergeSpokenTimes": " times ",
    "flashCards.mergeSpokenEquals": "{powers} equals {value}",
    "flashCards.mergeTargetGcf": "GCF of {a} and {b}",
    "flashCards.mergeStatusTemplate": "Targets made: {score} · Refused merges: {refused}",
    "flashCards.mergeSelectedTemplate": "{n} selected. Tap another tile to merge them, or tap {n} again to put it back.",
    "flashCards.mergeMergedTemplate": "{a} × {b} = {product}.",
    "flashCards.mergeRefusedTemplate": "{a} × {b} = {product}, and {product} is not a factor of {list}.",
    "flashCards.mergeRefusedAnyTemplate": "{a} × {b} = {product}, which is not a factor of any target.",
    "flashCards.mergeCalmerPrefix": "Rather practise the facts one at a time? Try the ",
    "flashCards.mergeCalmerLink": "multiplication quiz",
    "flashCards.mergeCalmerSuffix": ".",
    "flashCards.mergeListTwo": "{a} or {b}",
    "flashCards.mergeListThree": "{a}, {b} or {c}",
    "flashCards.mergeMadeTemplate": "Target made: {line}.",
    "flashCards.mergeMadeLcmTemplate": "Target made: the LCM of {a} and {b} is {value}. {line}.",
    "flashCards.mergeMadeLcm3Template": "Target made: the LCM of {a}, {b} and {c} is {value}. {line}.",
    "flashCards.mergeMadeGcfTemplate": "Target made: the GCF of {a} and {b} is {value}. {line}.",
    "flashCards.mergeClearedTemplate": "Removed {n}.",
    "flashCards.mergeClearOn": "Clear mode: tap a tile to remove it. Press the button again, or Esc, to cancel.",
    "flashCards.mergeClearOff": "Clear cancelled.",
    "flashCards.mergeClearButtonTemplate": "Clear a tile ({n} left)",
    "flashCards.mergeOver": "No merge is left. The run is over.",
    "flashCards.mergeFinal": "Run over",
    "flashCards.mergeScoreCaption": "targets made",
    "flashCards.mergeSummaryRefusedTemplate": "Merges refused: {n}",
    "flashCards.mergePracticeHeading": "Practise away from the game",
    // Shop Rush (ADR 0131).
    "flashCards.shopHowToPlay": "Ten customers come to the counter, one at a time. Fill each order, then press Serve. Right the first time is 2 stars, right on the second try is 1 star, and a customer who gets it wrong twice leaves. A shift has 20 stars to earn.",
    "flashCards.shopLevelsLabel": "Level",
    "flashCards.shopLevelG2": "Grade 2",
    "flashCards.shopLevelG3": "Grade 3",
    "flashCards.shopLevelG4": "Grade 4",
    "flashCards.shopLevelG5": "Grade 5",
    "flashCards.shopLevelG6": "Grade 6",
    "flashCards.shopLevelG7": "Grade 7",
    "flashCards.shopLevelG8": "Grade 8",
    "flashCards.shopLevelNoteG2": "Counting coins, change from $1.00, adding prices and fractions of a pizza.",
    "flashCards.shopLevelNoteG3": "Change from $1, $2 and $5, adding prices, times tables and fractions of a pizza.",
    "flashCards.shopLevelNoteG4": "Change to $10, bigger bills, one digit times two and pizza fractions.",
    "flashCards.shopLevelNoteG5": "Change to $20, bills at any cent, two digits times two and pizza fractions.",
    "flashCards.shopLevelNoteG6": "Discounts and tips at friendly percents, unit price and the better buy.",
    "flashCards.shopLevelNoteG7": "Discounts, sales tax, tips and markup.",
    "flashCards.shopLevelNoteG8": "Discount then tax, original price, percent change and simple interest.",
    "flashCards.shopPacesLabel": "Pace",
    "flashCards.shopPaceCalm": "Calm",
    "flashCards.shopPaceBusy": "Busy",
    "flashCards.shopPaceRush": "Rush",
    "flashCards.shopPaceNoClock": "No clock",
    "flashCards.shopPaceSecondsTemplate": "{n} seconds a customer",
    "flashCards.shopStart": "Start the shift",
    "flashCards.shopCalmerPrefix": "Rather practise sums one at a time? Try the ",
    "flashCards.shopCalmerLink": "addition quiz",
    "flashCards.shopCalmerSuffix": ".",
    "flashCards.shopStatusTemplate": "Customer {n} of {total} · Stars: {stars} of {max}",
    "flashCards.shopCounterLabel": "The counter",
    "flashCards.shopPatienceLabel": "Patience",
    "flashCards.shopPause": "Pause",
    "flashCards.shopResume": "Resume",
    "flashCards.shopPausedNote": "Paused. Press Resume to carry on.",
    "flashCards.shopServe": "Serve",
    "flashCards.shopNextCustomer": "Next customer",
    "flashCards.shopFinish": "Finish the shift",
    "flashCards.shopUndo": "Undo",
    "flashCards.shopClear": "Clear",
    "flashCards.shopBackspace": "Backspace",
    "flashCards.shopGivenTemplate": "Given: {amount}",
    "flashCards.shopAddTemplate": "Add {amount}",
    "flashCards.shopTrayLabel": "Coins and bills",
    "flashCards.shopKeypadLabel": "Till keypad",
    "flashCards.shopEntryLabel": "Your answer",
    "flashCards.shopOffersLabel": "The two offers",
    "flashCards.shopOfferTemplate": "{count} for {price}",
    "flashCards.shopBoardLabel": "Price board",
    "flashCards.shopCoinsLabel": "The coins",
    "flashCards.shopArriveTemplate": "Customer {n}. {order}",
    "flashCards.shopOrderChange": "A snack costs {price}. The customer pays {paid}. Give the change.",
    "flashCards.shopOrderTotal": "Ring up {lines}. What is the total?",
    "flashCards.shopOrderGroups": "{groups} {container} of {each} {thing}: how many {thing} in all?",
    "flashCards.shopOrderFraction": "{fraction} of a pizza cut into {slices} slices: how many slices?",
    "flashCards.shopOrderDiscountSale": "{rate}% off {price}: what is the sale price?",
    "flashCards.shopOrderDiscountOff": "{rate}% off {price}: how much is taken off?",
    "flashCards.shopOrderTax": "{price} plus {rate}% tax: what is the total?",
    "flashCards.shopOrderTip": "The meal costs {price}. How much is the {rate}% tip?",
    "flashCards.shopOrderBetterBuy": "{a}, or {b}: which costs less each?",
    "flashCards.shopOrderCoins": "The customer pays with these coins. How much money is that?",
    "flashCards.shopOrderUnitPrice": "{count} {items} for {total}: how much is one?",
    "flashCards.shopOrderMarkup": "The shop paid {cost} for it and marks it up {rate}%. What is the selling price?",
    "flashCards.shopOrderDiscountTax": "{price}, {discount}% off, then {tax}% tax: what is the total?",
    "flashCards.shopOrderOriginalPrice": "After {rate}% off, it costs {sale}. What was the original price?",
    "flashCards.shopOrderPercentUp": "The price went from {from} to {to}. By what percent did it go up?",
    "flashCards.shopOrderPercentDown": "The price went from {from} to {to}. By what percent did it go down?",
    "flashCards.shopOrderInterest": "{principal} at {rate}% a year for {years} years: how much interest?",
    "flashCards.shopOrderInterestOne": "{principal} at {rate}% a year for 1 year: how much interest?",
    "flashCards.shopServedFirst": "Thank you! Two stars.",
    "flashCards.shopServedSecond": "Thank you! One star.",
    "flashCards.shopMissTemplate": "Not quite. {hint} Try again.",
    "flashCards.shopLeftWrongTemplate": "The customer left. {worked}",
    "flashCards.shopLeftPatienceTemplate": "Out of patience: the customer left. {worked}",
    "flashCards.shopItemApple": "apple",
    "flashCards.shopItemApplePlural": "apples",
    "flashCards.shopItemBanana": "banana",
    "flashCards.shopItemBananaPlural": "bananas",
    "flashCards.shopItemCookie": "cookie",
    "flashCards.shopItemCookiePlural": "cookies",
    "flashCards.shopItemJuice": "juice box",
    "flashCards.shopItemJuicePlural": "juice boxes",
    "flashCards.shopItemMilk": "carton of milk",
    "flashCards.shopItemMilkPlural": "cartons of milk",
    "flashCards.shopItemBread": "loaf of bread",
    "flashCards.shopItemBreadPlural": "loaves of bread",
    "flashCards.shopItemSandwich": "sandwich",
    "flashCards.shopItemSandwichPlural": "sandwiches",
    "flashCards.shopItemNotebook": "notebook",
    "flashCards.shopItemNotebookPlural": "notebooks",
    "flashCards.shopItemSticker": "sticker",
    "flashCards.shopItemStickerPlural": "stickers",
    "flashCards.shopItemPencil": "pencil",
    "flashCards.shopItemPencilPlural": "pencils",
    "flashCards.shopContainerBags": "bags",
    "flashCards.shopContainerBoxes": "boxes",
    "flashCards.shopContainerTrays": "trays",
    "flashCards.shopContainerBaskets": "baskets",
    "flashCards.shopContainerPacks": "packs",
    "flashCards.shopContainerJars": "jars",
    "flashCards.shopContainerCrates": "crates",
    "flashCards.shopContainerCartons": "cartons",
    "flashCards.shopThingRolls": "rolls",
    "flashCards.shopThingCrayons": "crayons",
    "flashCards.shopThingMuffins": "muffins",
    "flashCards.shopThingApples": "apples",
    "flashCards.shopThingStickers": "stickers",
    "flashCards.shopThingCookies": "cookies",
    "flashCards.shopThingOranges": "oranges",
    "flashCards.shopThingEggs": "eggs",
    "flashCards.shopHintChange": "Count up from {price} to {paid}.",
    "flashCards.shopHintTotal": "Add the dollars, then the cents.",
    "flashCards.shopHintGroups": "{groups} groups of {each}.",
    "flashCards.shopHintFraction": "Split {slices} slices into {den} equal parts: {slices} ÷ {den} = {per}.",
    "flashCards.shopHintFractionSplit": "Split {slices} slices into {den} equal parts.",
    "flashCards.shopHintPercentOf": "Find {rate}% of {price} first.",
    "flashCards.shopHintTenthBuild": "10% of {price} is {tenth}. Build {rate}% from that.",
    "flashCards.shopHintTenthDivide": "10% is a tenth: divide {price} by 10.",
    "flashCards.shopHintTenth": "10% of {price} is {tenth}.",
    "flashCards.shopHintQuarter": "25% is a quarter: divide {price} by 4.",
    "flashCards.shopHintTip15": "10% of {price} is {tenth}, and 5% is half of that.",
    "flashCards.shopHintTip18": "10% of {price} is {tenth}. Find 20%, then take off 2%.",
    "flashCards.shopHintBetterBuy": "Find the price of one in each offer.",
    "flashCards.shopHintCoins": "Count the quarters first, then the dimes, nickels and pennies.",
    "flashCards.shopHintTotalCents": "Add the prices one at a time.",
    "flashCards.shopHintFractionSlice": "Each slice is 1/{den} of the pizza. Count how many slices make {fraction}.",
    "flashCards.shopHintUnitPrice": "Share {total} equally among {count}.",
    "flashCards.shopHintDiscountTax": "Take off the discount first, then add tax on the sale price.",
    "flashCards.shopHintOriginalPrice": "After {rate}% off, {sale} is {rest}% of the price.",
    "flashCards.shopHintPercentChange": "Find the change, then divide it by the starting price.",
    "flashCards.shopHintInterest": "One year's interest is {rate}% of {principal}.",
    "flashCards.shopWorkedChange": "{steps}, so the change is {change}.",
    "flashCards.shopWorkedTotal": "{terms} = {total}.",
    "flashCards.shopWorkedGroups": "{groups} × {each} = {answer}.",
    "flashCards.shopWorkedFraction": "{fraction} of {slices}: {slices} ÷ {den} = {per}, then {per} × {num} = {answer}.",
    "flashCards.shopWorkedDiscountSale": "{rate}% of {price} is {off}; {price} − {off} = {sale}.",
    "flashCards.shopWorkedDiscountOff": "{rate}% of {price} is {off}.",
    "flashCards.shopWorkedTax": "{rate}% of {price} is {tax}; {price} + {tax} = {total}.",
    "flashCards.shopWorkedTip10": "10% of {bill} = {bill} ÷ 10 = {tip}.",
    "flashCards.shopWorkedTip15": "10% = {tenth} and 5% = {half}, so 15% = {tenth} + {half} = {tip}.",
    "flashCards.shopWorkedTip18": "20% = {twenty} and 2% = {two}, so 18% = {twenty} − {two} = {tip}.",
    "flashCards.shopWorkedTip20": "10% = {tenth}, so 20% = {tenth} × 2 = {tip}.",
    "flashCards.shopWorkedTip25": "25% of {bill} = {bill} ÷ 4 = {tip}.",
    "flashCards.shopWorkedBetterBuy": "{priceA} ÷ {countA} = {unitA}, and {priceB} ÷ {countB} = {unitB}, so {winnerCount} for {winnerPrice}.",
    "flashCards.shopWorkedCoins": "{terms} = {total}.",
    "flashCards.shopWorkedUnitPrice": "{total} ÷ {count} = {unit}.",
    "flashCards.shopWorkedDiscountTax": "{discount}% of {price} is {off}, so the sale price is {sale}; {tax}% of {sale} is {taxAmount}; {sale} + {taxAmount} = {total}.",
    "flashCards.shopWorkedOriginalPrice": "{rest}% is {sale}, so 10% is {tenth} and 100% is {original}.",
    "flashCards.shopWorkedPercentChange": "{big} − {small} = {diff}; {diff} ÷ {from} = {ratio}, so {percent}%.",
    "flashCards.shopWorkedInterest": "{rate}% of {principal} is {yearly} a year; {yearly} × {years} = {interest}.",
    "flashCards.shopWorkedInterestOne": "{rate}% of {principal} is {interest}.",
    "flashCards.shopFinal": "Shift over",
    "flashCards.shopScoreCaptionTemplate": "stars out of {max}",
    "flashCards.shopSummaryServedTemplate": "Orders served: {n} of {total}",
    "flashCards.shopSummaryWrongTemplate": "Wrong tries: {n}",
    "flashCards.shopSummaryWalkedTemplate": "Customers who ran out of patience: {n}",
    "flashCards.shopPracticeHeading": "Practise away from the game",
    // Hidden Picture (ADR 0133).
    "flashCards.pictureHowToPlay": "A picture is hiding under 25 tiles, and every tile is a fact. Tap a tile, type the answer and press Check. A right answer lifts the tile. A wrong one gets a second try, and after a second miss the answer is shown and the tile lifts anyway.",
    "flashCards.pictureWhichFacts": "Which facts?",
    "flashCards.pictureStart": "Start",
    "flashCards.pictureHintSnowman": "Something for winter is hiding under the tiles.",
    "flashCards.pictureHintHeart": "Something for Valentine's Day is hiding under the tiles.",
    "flashCards.pictureHintShamrock": "Something for St. Patrick's Day is hiding under the tiles.",
    "flashCards.pictureHintTulip": "Something for spring is hiding under the tiles.",
    "flashCards.pictureHintIcecream": "Something for summer is hiding under the tiles.",
    "flashCards.pictureHintApple": "Something for fall and back to school is hiding under the tiles.",
    "flashCards.pictureHintPumpkin": "Something for Halloween is hiding under the tiles.",
    "flashCards.pictureHintTurkey": "Something for Thanksgiving is hiding under the tiles.",
    "flashCards.pictureHintGift": "Something for the winter holidays is hiding under the tiles.",
    "flashCards.pictureCaptionSnowman": "A snowman for winter!",
    "flashCards.pictureCaptionHeart": "A heart for Valentine's Day!",
    "flashCards.pictureCaptionShamrock": "A shamrock for St. Patrick's Day!",
    "flashCards.pictureCaptionTulip": "A tulip for spring!",
    "flashCards.pictureCaptionIcecream": "An ice cream cone for summer!",
    "flashCards.pictureCaptionApple": "An apple for back to school!",
    "flashCards.pictureCaptionPumpkin": "A jack-o'-lantern for Halloween!",
    "flashCards.pictureCaptionTurkey": "A turkey for Thanksgiving!",
    "flashCards.pictureCaptionGift": "A wrapped gift for the winter holidays!",
    "flashCards.pictureLeftTemplate": "Tiles left: {n}",
    "flashCards.pictureRightTemplate": "Right first time: {n}",
    "flashCards.pictureBoardLabel": "Tiles. Pick one to answer.",
    "flashCards.pictureTileAriaTemplate": "Row {row}, tile {col}: {fact}",
    "flashCards.pictureCheck": "Check",
    "flashCards.pictureRight": "Right!",
    "flashCards.pictureTryAgain": "Not quite. Try again.",
    "flashCards.pictureAnswerShownTemplate": "The answer is {fact} = {answer}.",
    "flashCards.pictureAnnounceRightTemplate": "Right. Tiles left: {n}.",
    "flashCards.pictureAnnounceWrongTemplate": "Not quite. Try again: {fact}.",
    "flashCards.pictureAnnounceMissedTemplate": "The answer is {fact} = {answer}. Tiles left: {n}.",
    "flashCards.pictureAnnounceNextTemplate": "Next: {fact}.",
    "flashCards.pictureAnnounceDone": "The picture is finished.",
    "flashCards.pictureHint": "Tap any tile to answer that one instead. Press Enter to check.",
    "flashCards.pictureFinal": "The picture is finished",
    "flashCards.pictureScoreTemplate": "{n} of {total} tiles right the first time",
    "flashCards.pictureSummaryWrongTemplate": "Wrong tries: {n}",
    "flashCards.pictureMoreHeading": "Keep practising",
    // Slingshot Plate Smash (ADR 0060).
    "flashCards.platesHowToPlay": "Read the fact on the sign, then smash the plate with the answer. Drag back on the booth and let go to shoot. You have 60 seconds: 10 points for a first-shot hit, and double once you hit three in a row.",
    "flashCards.platesWhichFacts": "Which facts?",
    "flashCards.platesChooseMode": "Choose a booth to start",
    "flashCards.platesEasy": "Easy",
    "flashCards.platesHard": "Hard",
    "flashCards.platesEasyHint": "Plates stand still",
    "flashCards.platesHardHint": "Plates ride a conveyor belt",
    "flashCards.platesBoothAria": "Slingshot booth. Drag back and let go to shoot, or use the arrow keys to aim and Space to shoot.",
    "flashCards.platesHint": "Drag back and let go to shoot. Arrow keys aim, Space shoots.",
    "flashCards.platesAimingAtTemplate": "Aiming at {n}",
    "flashCards.platesAimingAtNothing": "Aiming between the plates",
    "flashCards.platesHotStreak": "Hot streak ×2",
    "flashCards.platesVerdictHitTemplate": "Smash! +{n}",
    "flashCards.platesVerdictLateTemplate": "There it is: {n}. No points after a wrong plate.",
    "flashCards.platesVerdictWrongTemplate": "That plate was {n}. Try again!",
    "flashCards.platesVerdictMiss": "Missed the plates",
    "flashCards.platesTimeUp": "Time's up!",
    "flashCards.platesPointsCaption": "points",
    "flashCards.platesCountsTemplate": "{hits} smashed first shot · {wrong} wrong plates",
    "flashCards.platesMadeTheBoard": "You made the leaderboard!",
    "flashCards.platesNameLabel": "Your name for the board",
    "flashCards.platesNamePlaceholder": "First name or nickname",
    "flashCards.platesSave": "Save",
    "flashCards.platesSkip": "Don't save this score",
    "flashCards.platesRankTemplate": "You're number {n} on the board!",
    "flashCards.platesYouHitTemplate": "you hit {n}",
    "flashCards.platesChangeMode": "Choose a different booth",
    "flashCards.platesLeaderboard": "Leaderboard",
    "flashCards.platesBoardEmpty": "No scores yet. Be the first!",
    "flashCards.platesBoardName": "Name",
    "flashCards.platesBoardScore": "Score",
    "flashCards.platesBoardFacts": "Facts",
    "flashCards.platesBoardNote": "Saved in this browser only. Nothing is sent anywhere.",
    "flashCards.platesClear": "Clear leaderboard",
    "flashCards.platesClearConfirm": "Clear every score and name?",
    "flashCards.platesClearYes": "Clear",
    "flashCards.platesClearNo": "Keep",
    // The Baker Street Math Crossword (ADR 0134).
    "flashCards.crosswordHowToPlay": "Every line of the grid is a number sentence, reading across or down, and most numbers are in two lines at once. Fill the blanks so that every line is true. Close five cases to finish the casebook. Each case is worth up to 3 magnifying glasses: Ask Holmes, and a Check that finds a mistake, each cost one, and a case you solve always earns at least one.",
    "flashCards.crosswordRanksLabel": "Rank",
    "flashCards.crosswordRankConstable": "Constable",
    "flashCards.crosswordRankInspector": "Inspector",
    "flashCards.crosswordRankDetective": "Detective",
    "flashCards.crosswordRankGradesConstable": "Grades 1–2",
    "flashCards.crosswordRankGradesInspector": "Grades 3–4",
    "flashCards.crosswordRankGradesDetective": "Grades 5–6",
    "flashCards.crosswordRankNoteConstable": "Adding and taking away within 20, on a grid of nine numbers. A bank of the missing numbers sits on the desk.",
    "flashCards.crosswordRankNoteInspector": "Adding and taking away to 100, or times and divide within the 10 × 10 facts, on a grid of nine numbers. A bank of the missing numbers sits on the desk.",
    "flashCards.crosswordRankNoteDetective": "A bigger grid with three numbers in each line, so times and divide come before add and take away. No bank of numbers.",
    "flashCards.crosswordStart": "Open the casebook",
    "flashCards.crosswordCalmerPrefix": "Rather practise sums one at a time? Try the ",
    "flashCards.crosswordCalmerLink": "addition quiz",
    "flashCards.crosswordCalmerSuffix": ".",
    "flashCards.crosswordPlateStreet": "Baker Street",
    "flashCards.crosswordStatusTemplate": "Case {n} of {total} · Magnifying glasses: {glasses} of {max}",
    "flashCards.crosswordWorthTemplate": "This case: {n} of {max}",
    "flashCards.crosswordBoardLabel": "The crossword",
    "flashCards.crosswordLinesLabel": "The lines",
    "flashCards.crosswordBankLabel": "Missing numbers. Pick one to put it in the selected blank.",
    "flashCards.crosswordCheck": "Check",
    "flashCards.crosswordAsk": "Ask Holmes",
    "flashCards.crosswordNextCase": "Next case",
    "flashCards.crosswordFinish": "Close the casebook",
    "flashCards.crosswordArriveTemplate": "Case {n}. {title}. {brief}",
    "flashCards.crosswordSolvedTemplate": "Elementary. Case closed with {n} of {max} magnifying glasses.",
    "flashCards.crosswordCheckWrongOne": "One number is not right. It is marked. That costs a magnifying glass.",
    "flashCards.crosswordCheckWrongMany": "{n} numbers are not right. They are marked. That costs a magnifying glass.",
    "flashCards.crosswordCheckRight": "Everything filled in so far is right.",
    "flashCards.crosswordCheckEmpty": "Fill in a number first, then check.",
    "flashCards.crosswordHintTemplate": "{line} says {open}. Only {value} works: {closed}.",
    "flashCards.crosswordLineAcross": "Across {n}",
    "flashCards.crosswordLineDown": "Down {n}",
    "flashCards.crosswordTagAcross": "A{n}",
    "flashCards.crosswordTagDown": "D{n}",
    "flashCards.crosswordPositionFirst": "first number",
    "flashCards.crosswordPositionSecond": "second number",
    "flashCards.crosswordPositionThird": "third number",
    "flashCards.crosswordPositionAnswer": "answer",
    "flashCards.crosswordCellLabelTemplate": "{line}, {position}",
    "flashCards.crosswordLineReadingTemplate": "{line}: {sentence}",
    "flashCards.crosswordBlankWord": "blank",
    "flashCards.crosswordFinal": "Case closed",
    "flashCards.crosswordScoreCaptionTemplate": "magnifying glasses out of {max}",
    "flashCards.crosswordSummaryUnaidedTemplate": "Cases solved without Ask Holmes: {n} of {total}",
    "flashCards.crosswordSummaryWrongTemplate": "Wrong numbers found by Check: {n}",
    "flashCards.crosswordSummaryHolmesTemplate": "Numbers Holmes filled in: {n}",
    "flashCards.crosswordPracticeHeading": "Practise away from the game",
    "flashCards.crosswordCase1Title": "The Case of the Smudged Ledger",
    "flashCards.crosswordCase1Brief": "Mrs Hudson's accounts have run in the rain. Work out the figures the damp has taken.",
    "flashCards.crosswordCase2Title": "The Case of the Midnight Telegram",
    "flashCards.crosswordCase2Brief": "A telegram reached Baker Street at midnight, but some of its figures were torn away.",
    "flashCards.crosswordCase3Title": "The Case of the Silent Clockmaker",
    "flashCards.crosswordCase3Brief": "The clockmaker's price list was found on his bench, with a few of its numbers rubbed out.",
    "flashCards.crosswordCase4Title": "The Case of the Foggy Wharf",
    "flashCards.crosswordCase4Brief": "Fog has crept over a cargo list from the river wharf and hidden some of the figures.",
    "flashCards.crosswordCase5Title": "The Case of the Locked Cash Box",
    "flashCards.crosswordCase5Brief": "Inspector Lestrade has the box open, but its tally sheet is missing a few numbers.",
    "flashCards.crosswordCase6Title": "The Case of the Forgotten Umbrella",
    "flashCards.crosswordCase6Brief": "An umbrella came back to Baker Street with a page of figures tied to it, and half are blotted.",
    "flashCards.crosswordCase7Title": "The Case of the Leaning Lamp Post",
    "flashCards.crosswordCase7Brief": "The lamplighter wrote down his round, but the gaslight has faded some of the numbers.",
    "flashCards.crosswordCase8Title": "The Case of the Curious Tea Tin",
    "flashCards.crosswordCase8Brief": "Mrs Hudson found a sum inside an old tea tin, and a few of its numbers are smudged.",
    "flashCards.crosswordCase9Title": "The Case of the Whispering Staircase",
    "flashCards.crosswordCase9Brief": "Someone slipped a page of sums under the door of 221B, and some of its numbers are gone.",
    "flashCards.crosswordCase10Title": "The Case of the Faded Postmark",
    "flashCards.crosswordCase10Brief": "Parcels at the post office have faded labels. Fill in the figures so that every line is true.",
    "flashCards.crosswordCase11Title": "The Case of the Wandering Cabman",
    "flashCards.crosswordCase11Brief": "A cab driver kept a tally of his fares, and the rain has washed some of it away.",
    "flashCards.crosswordCase12Title": "The Case of the Borrowed Violin",
    "flashCards.crosswordCase12Brief": "Dr Watson found a page of figures in the violin case. Help Holmes read it.",
} as const;

export type FlashCardsStringKey = keyof typeof en;

const es = {
    "flashCards.ariaFlippedTemplate": "{front} es igual a {back}. Toca para ocultar la respuesta.",
    "flashCards.ariaUnflippedTemplate": "{front}. Toca para mostrar la respuesta.",
    "flashCards.tapToFlip": "Toca para voltear",
    "flashCards.emptyDeck": "Elige una operación y al menos un número para empezar un mazo.",
    "flashCards.cardProgress": "Tarjeta {current} de {total}",
    "flashCards.previous": "Anterior",
    "flashCards.next": "Siguiente",
    "flashCards.newDeck": "Nuevo mazo",
    "flashCards.swipeHint":
        "Desliza o usa las flechas para moverte entre las tarjetas; espacio para voltear.",
    "flashCards.changeWhatToPractice": "Cambiar lo que se practica",
    "flashCards.frequentlyAskedQuestions": "Preguntas frecuentes",
    "flashCards.printTheseCards": "Imprimir estas tarjetas didácticas",
    "flashCards.cards": "tarjetas",
    "flashCards.printIntroPrefix":
        "{count} tarjetas por hoja, del tamaño de una ficha. Recorta por las líneas discontinuas. ",
    "flashCards.printIntroDoubleSided":
        "Imprime a doble cara, girando por el lado largo, y cada respuesta queda al dorso de su tarjeta.",
    "flashCards.printIntroSingleSided":
        "Las respuestas al dorso están desactivadas, así que las tarjetas se imprimen solo con el problema.",
    "flashCards.sheetsOfCardsLabel": "Hojas de tarjetas: ",
    "flashCards.addSheetAria": "Añadir una hoja de tarjetas",
    "flashCards.removeSheetAria": "Quitar una hoja de tarjetas",
    "flashCards.answersOnBackLabel": "Respuestas al dorso: ",
    "flashCards.withAnswerBacks": ", con respuestas al dorso",
    "flashCards.onSheetsSuffix": " en {n} {sheetWord}",
    "flashCards.sheet": "hoja",
    "flashCards.sheets": "hojas",
    "flashCards.cappedByPoolNote":
        "Cada tarjeta es distinta, y estos ajustes solo generan {count} {factWord} de " +
        "{operation} diferentes: eso es todo el conjunto. Amplía el rango de números para tener más.",
    "flashCards.fact": "operación",
    "flashCards.facts": "operaciones",
    "flashCards.chooseOperationNote":
        "Elige una operación y al menos un número para imprimir un juego de tarjetas.",
    "flashCards.loadingPreview": "Cargando vista previa…",
    "flashCards.openFlashCardsPdf": "Abrir el PDF de las tarjetas didácticas",
    "flashCards.buildingCards": "Preparando tus tarjetas…",
    "flashCards.previewOfFlashCardsPage1": "Vista previa de la página 1 de las tarjetas",
    "flashCards.missedFactsCountTemplate": "{count} {factWord} para repasar de nuevo.",
    "flashCards.printForPractice": "Imprimir esto para practicar",
    "flashCards.loadingPracticeSheet": "Cargando hoja de práctica…",
    "flashCards.quizEmptyNote":
        "Elige una operación y al menos un número abajo para empezar un cuestionario.",
    "flashCards.chooseDifferentLength": "Elegir otra duración",
    "flashCards.howLongDoYouWant": "¿Cuánto tiempo quieres?",
    "flashCards.noClockJustPractise": "Sin cronómetro: solo practicar",
    "flashCards.changeWhatToPractise": "Cambiar lo que se practica",
    "flashCards.thirtySeconds": "30 segundos",
    "flashCards.oneMinute": "1 minuto",
    "flashCards.twoMinutes": "2 minutos",
    "flashCards.threeMinutes": "3 minutos",
    "flashCards.timeLeftAria": "Tiempo restante",
    "flashCards.scoreTemplate": "Puntuación {n}",
    "flashCards.streakTemplate": "Racha {n}",
    "flashCards.getReady": "Prepárate…",
    "flashCards.answerForAriaTemplate": "Respuesta para {front}",
    "flashCards.enterButton": "Intro",
    "flashCards.correct": "Correcto",
    "flashCards.skipped": "Omitido",
    "flashCards.notQuite": "No exactamente",
    "flashCards.finishButton": "Terminar",
    "flashCards.quizHint":
        "Escribe la respuesta y pulsa Intro. Pulsa Intro con la casilla vacía para omitir.",
    "flashCards.noClockHintSuffix": " No hay cronómetro: para cuando quieras.",
    "flashCards.correctCaption": "respuestas correctas",
    "flashCards.againstTheClockSuffix": " contra el reloj",
    "flashCards.answeredCountsTemplate":
        "{answered} respondidas · {wrong} incorrectas · {skipped} omitidas",
    "flashCards.newBest": "¡Nuevo récord!",
    "flashCards.yourBestTemplate": "Tu récord: {n}",
    "flashCards.worthAnotherLookHeading": "Vale la pena repasar",
    "flashCards.skippedLower": "omitido",
    "flashCards.youPutTemplate": "pusiste {typed}",
    "flashCards.playAgain": "Jugar otra vez",
    "flashCards.gameEmptyNote":
        "Elige una operación y al menos un número abajo para empezar el juego.",
    "flashCards.gameHowToPlay":
        "Las operaciones caen desde arriba. Escribe la respuesta y pulsa Enter para eliminar una antes de que llegue al suelo. Si llegan tres, se acaba el juego.",
    "flashCards.gameStart": "Empezar el juego",
    "flashCards.gameCalmerPrefix": "¿Prefieres practicar sin prisas? Prueba el ",
    "flashCards.gameCalmerLink": "cuestionario sin reloj",
    "flashCards.gameCalmerSuffix": ".",
    "flashCards.gameLevelTemplate": "Nivel {n}",
    "flashCards.gameLivesAriaTemplate": "Te quedan {n} vidas",
    "flashCards.gameOneLifeAria": "Te queda 1 vida",
    "flashCards.gameLastLife": "Una operación llegó al suelo. Te queda una vida.",
    "flashCards.gamePause": "Pausa",
    "flashCards.gameResume": "Seguir",
    "flashCards.gamePaused": "En pausa",
    "flashCards.gamePausedHint": "Las operaciones están ocultas hasta que sigas.",
    "flashCards.gameAnswerAria": "Escribe una respuesta para eliminar una operación que cae",
    "flashCards.gameHint": "Escribe la respuesta y pulsa Enter. Un intento fallido solo cuesta tiempo.",
    "flashCards.gameLifeLostTemplate": "Una operación llegó al suelo. Te quedan {n} vidas.",
    "flashCards.gameLevelUpTemplate": "Nivel {n}. Ahora caen más rápido.",
    "flashCards.gameOver": "Fin del juego",
    "flashCards.gameClearedCaption": "operaciones eliminadas",
    "flashCards.gameReachedTemplate": "Llegaste al nivel {level} · {wrong} intentos fallidos",
    "flashCards.dropChangeFacts": "Elegir otras operaciones",
    "flashCards.chartIntro": "Responde una operación para rellenar su casilla y su reflejo al otro lado de la diagonal. Toca cualquier casilla vacía para elegirla tú. Tu tabla se guarda en este dispositivo.",
    "flashCards.chartProgressTemplate": "{filled} de {total} operaciones",
    "flashCards.chartHint": "Escribe la respuesta y pulsa Enter. Enter con la casilla vacía salta a otra.",
    "flashCards.chartStartOver": "Empezar una tabla nueva",
    "flashCards.chartConfirmStartOver": "¿Borrar toda tu tabla?",
    "flashCards.chartConfirmYes": "Sí, borrarla",
    "flashCards.chartConfirmNo": "Conservarla",
    "flashCards.chartComplete": "¡Has completado toda la tabla!",
    "flashCards.chartCompleteDetailTemplate": "Las {total} operaciones · {wrong} respuestas incorrectas por el camino",
    "flashCards.chartGridCaption": "Tu tabla de multiplicar",
    "flashCards.chartCellEmptyAriaTemplate": "{a} por {b}: vacía. Preguntar esta.",
    "flashCards.chartCellTrickyAriaTemplate": "{a} por {b}: difícil. Preguntar esta.",
    "flashCards.chartCellFilledAriaTemplate": "{a} por {b}: {answer}",
    "flashCards.chartWhichChart": "¿Qué tabla?",
    "flashCards.chartPickAddition": "Suma",
    "flashCards.chartPickMultiplication": "Multiplicación",
    "flashCards.chartGridCaptionAddition": "Tu tabla de sumar",
    "flashCards.chartCellEmptyAdditionAriaTemplate": "{a} más {b}: vacía. Preguntar esta.",
    "flashCards.chartCellTrickyAdditionAriaTemplate": "{a} más {b}: difícil. Preguntar esta.",
    "flashCards.chartCellFilledAdditionAriaTemplate": "{a} más {b}: {answer}",
    "flashCards.productModePrompt": "¿Contra quién juegas?",
    "flashCards.productVsComputer": "Jugar contra el ordenador",
    "flashCards.productTwoPlayers": "Dos jugadores, una pantalla",
    "flashCards.productHowTo": "Mueve un clip en cada turno y escribe el producto de los dos clips para quedarte esa casilla. Consigue cuatro en línea para ganar.",
    "flashCards.productYourTurn": "Tu turno",
    "flashCards.productComputerThinking": "El ordenador está pensando…",
    "flashCards.productPlayerTurnTemplate": "Turno del jugador {n}",
    "flashCards.productPlaceFirstClip": "Pon el clip A en cualquier factor. En el primer movimiento no se gana ninguna casilla.",
    "flashCards.productPlaceSecondClip": "Pon el clip B en un factor y escribe el producto.",
    "flashCards.productMoveOneClip": "Mueve el clip A o el clip B a otro factor.",
    "flashCards.productTypeProductTemplate": "¿Cuánto es {front}? Escribe la respuesta para quedarte la casilla.",
    "flashCards.productClipLabelTemplate": "Clip {name}",
    "flashCards.productMoveClipAriaTemplate": "Mover el clip {name} al {n}",
    "flashCards.productComputerMovedTemplate": "El ordenador movió el clip {name} al {n} y se quedó el {product}.",
    "flashCards.productYouWin": "Cuatro en línea: ¡has ganado!",
    "flashCards.productComputerWins": "El ordenador consiguió cuatro en línea.",
    "flashCards.productPlayerWinsTemplate": "¡El jugador {n} gana con cuatro en línea!",
    "flashCards.productDraw": "No quedan movimientos: es un empate.",
    "flashCards.productChangeMode": "Cambiar contra quién juegas",
    "flashCards.productBoardCaption": "Tablero del juego de productos",
    "flashCards.productCellFreeTemplate": "{product}: libre",
    "flashCards.productCellYoursTemplate": "{product}: tuya",
    "flashCards.productCellComputerTemplate": "{product}: del ordenador",
    "flashCards.productCellPlayerTemplate": "{product}: del jugador {n}",
    "flashCards.productTurnPasses": "Pasa el turno.",
    "flashCards.productVariantPrompt": "¿A qué juego quieres jugar?",
    "flashCards.productVariantProduct": "Juego de productos (×)",
    "flashCards.productVariantSum": "Juego de sumas (+)",
    "flashCards.productSumHowTo": "Mueve un clip en cada turno y escribe la suma de los dos clips para quedarte una casilla con esa suma. Si hay más de una libre, tú eliges cuál. Consigue cuatro en línea para ganar.",
    "flashCards.productSumPlaceFirstClip": "Pon el clip A en cualquier número. En el primer movimiento no se gana ninguna casilla.",
    "flashCards.productSumPlaceSecondClip": "Pon el clip B en un número y escribe la suma.",
    "flashCards.productSumMoveOneClip": "Mueve el clip A o el clip B a otro número.",
    "flashCards.productSumTypeTemplate": "¿Cuánto es {front}? Escribe la respuesta para quedarte una casilla.",
    "flashCards.productChooseSquare": "Elige qué casilla quieres quedarte",
    "flashCards.productSumComputerMovedTemplate": "El ordenador movió el clip {name} al {n} y se quedó una casilla con el {product}.",
    "flashCards.productSumBoardCaption": "Tablero del juego de sumas",
    "flashCards.productCellChooseTemplate": "{product}: quedarse esta casilla",
    "flashCards.munchModePrompt": "¿Cómo quieres jugar?",
    "flashCards.munchWithMonsters": "Con monstruos",
    "flashCards.munchCalm": "Tranquilo: sin monstruos",
    "flashCards.munchHowTo": "Muévete con las flechas o toca una casilla a tu lado. Pulsa Espacio, o tócate a ti mismo, para comer. Come todas las casillas que cumplan la regla; comer una equivocada cuesta una vida.",
    "flashCards.munchRuleMultiples": "Múltiplos de {n}",
    "flashCards.munchRuleProducts": "Da {n}",
    "flashCards.munchRuleFactors": "Divisores de {n}",
    "flashCards.munchLeftTemplate": "Quedan {n} por comer",
    "flashCards.munchLevelDone": "¡Nivel completado!",
    "flashCards.munchWhyMultipleTemplate": "{v} no es múltiplo de {n}: {n} × {k} = {lo} y {n} × {k1} = {hi}.",
    "flashCards.munchWhyMultipleSmallTemplate": "{v} no es múltiplo de {n}: el menor múltiplo de {n} es {n}.",
    "flashCards.munchWhyFactorTemplate": "{v} no es divisor de {n}: {n} ÷ {v} = {q} y sobra {r}.",
    "flashCards.munchWhyProductTemplate": "{front} = {p}, no {n}.",
    "flashCards.munchLostLife": "Pierdes una vida.",
    "flashCards.munchCaught": "¡Te ha atrapado un monstruo! Pierdes una vida.",
    "flashCards.munchYumTemplate": "¡Ñam! {text} cumple la regla.",
    "flashCards.munchBoardLabel": "Tablero. Usa las flechas para moverte y Espacio para comer.",
    "flashCards.munchSquareEaten": "comida",
    "flashCards.munchSquareYou": "estás aquí",
    "flashCards.munchSquareMonster": "hay un monstruo",
    "flashCards.munchUp": "Arriba",
    "flashCards.munchDown": "Abajo",
    "flashCards.munchLeft": "Izquierda",
    "flashCards.munchRight": "Derecha",
    "flashCards.munchMunchButton": "Comer",
    "flashCards.munchScoreCaption": "casillas comidas",
    "flashCards.munchReachedTemplate": "Llegaste al nivel {level} · {wrong} casillas equivocadas",
    "flashCards.munchChangeMode": "Cambiar cómo juegas",
    "flashCards.munchWhichFacts": "¿Qué operaciones quieres practicar?",
    "flashCards.munchWhyAddTemplate": "{front} da {p}, no {n}.",
    "flashCards.munchWhySubtractTemplate": "{front} deja {p}, no {n}.",
    "flashCards.munchWhyDivideTemplate": "{front} da {p}, no {n}.",
    "flashCards.collieHowTo": "Ayuda a la border collie a cruzar el suelo. Solo las baldosas que cumplen la regla la aguantan; las demás se rompen y vuelve a la salida. Muévete con las flechas o toca una baldosa a su lado.",
    "flashCards.collieStart": "Empezar a cruzar",
    "flashCards.collieFloorTemplate": "Suelo {n}",
    "flashCards.collieFallsTemplate": "Caídas: {n}",
    "flashCards.collieFellTemplate": "¡Crac! {why} Vuelta a la salida.",
    "flashCards.collieSafeTemplate": "{text} aguanta.",
    "flashCards.collieCrossed": "¡Ha llegado al otro lado!",
    "flashCards.collieCrossedClean": "Sin ninguna caída.",
    "flashCards.collieCrossedOneFall": "Con una caída.",
    "flashCards.collieCrossedFallsTemplate": "Con {n} caídas.",
    "flashCards.collieNextFloor": "Siguiente suelo",
    "flashCards.collieOtherSide": "El otro lado",
    "flashCards.collieStartLedge": "Salida",
    "flashCards.collieBoardLabel": "Suelo de baldosas. Usa las flechas para mover a la perra.",
    "flashCards.collieTileBroken": "rota",
    "flashCards.collieTileSafe": "segura",
    "flashCards.collieDogHere": "la perra está aquí",
    "flashCards.collieStarsAriaTemplate": "{n} de 3 estrellas",
    "flashCards.collieWhichFacts": "¿Qué operaciones quieres practicar?",
    // Math Facts Soccer (ADR 0059).
    "flashCards.soccerHowToPlay": "Cada penalti es una operación. Escribe la respuesta y pulsa Enter para chutar. Date prisa: cuando el portero está listo, una respuesta correcta se para.",
    "flashCards.soccerWhichFacts": "¿Qué operaciones?",
    "flashCards.soccerChooseKeeper": "Elige tu portero para empezar",
    "flashCards.soccerKeeperRookie": "Novato",
    "flashCards.soccerKeeperPro": "Profesional",
    "flashCards.soccerKeeperLegend": "Leyenda",
    "flashCards.soccerKeeperSecondsTemplate": "{n} segundos",
    "flashCards.soccerGoalsTemplate": "Goles {n}",
    "flashCards.soccerKickTemplate": "Penalti {n} de {total}",
    "flashCards.soccerRecordAriaTemplate": "{goals} goles en {kicks} penaltis",
    "flashCards.soccerKeeperGettingReady": "El portero se prepara",
    "flashCards.soccerKeeperSetShort": "¡Portero listo!",
    "flashCards.soccerKeeperSet": "El portero está listo. Una respuesta correcta ahora se parará.",
    "flashCards.soccerAnswerAria": "Escribe la respuesta para tirar el penalti",
    "flashCards.soccerShoot": "Chutar",
    "flashCards.soccerHint": "Escribe la respuesta y pulsa Enter para chutar. Pulsa Enter con la casilla vacía para saltar.",
    "flashCards.soccerBannerGoal": "¡GOL!",
    "flashCards.soccerBannerHatTrick": "¡TRIPLETE!",
    "flashCards.soccerBannerSaved": "¡PARADA!",
    "flashCards.soccerBannerWide": "¡FUERA!",
    "flashCards.soccerBannerSkipped": "Saltado",
    "flashCards.soccerVerdictGoal": "¡Gol!",
    "flashCards.soccerVerdictSaved": "Bien, pero el portero estaba listo",
    "flashCards.soccerVerdictWide": "Fuera",
    "flashCards.soccerVerdictSkipped": "Saltado",
    "flashCards.soccerFullTime": "Final del partido",
    "flashCards.soccerGoalsFromTemplate": "goles en {total} penaltis",
    "flashCards.soccerCountsTemplate": "{saved} parados · {wide} fuera · {skipped} saltados",
    "flashCards.soccerSlowHeading": "Estas las sabías. Ahora, más rápido",
    "flashCards.soccerChangeKeeper": "Elegir otro portero",
    "flashCards.footballHowToPlay": "Cada jugada es una operación. Cuatro receptores corren sus rutas, cada uno con un número en la camiseta. Toca al que lleva la respuesta, o pulsa del 1 al 4, para lanzar. Lanza antes de que llegue la presión o te derriban. Un partido son cuatro series.",
    "flashCards.footballWhichFacts": "¿Qué operaciones?",
    "flashCards.footballChooseDefense": "Elige una defensa para empezar",
    "flashCards.footballDefenseRookie": "Novato",
    "flashCards.footballDefensePro": "Profesional",
    "flashCards.footballDefenseAllPro": "Élite",
    "flashCards.footballDefenseSecondsTemplate": "{n} segundos",
    "flashCards.footballPointsTemplate": "Puntos {n}",
    "flashCards.footballDriveTemplate": "Serie {n} de {total}",
    "flashCards.footballDownAndDistanceTemplate": "{down} y {distance}",
    "flashCards.footballDownFirst": "1.º",
    "flashCards.footballDownSecond": "2.º",
    "flashCards.footballDownThird": "3.º",
    "flashCards.footballDownFourth": "4.º",
    "flashCards.footballGoal": "gol",
    "flashCards.footballBallOnTemplate": "Balón en {spot}",
    "flashCards.footballOwnTemplate": "campo propio {n}",
    "flashCards.footballOppTemplate": "campo rival {n}",
    "flashCards.footballFieldAria": "El campo. Cuatro receptores corren hacia delante. Lanza al que lleva la respuesta.",
    "flashCards.footballReceiverAriaTemplate": "Receptor {n}: {value}",
    "flashCards.footballReceiverHiddenAriaTemplate": "Receptor {n}",
    "flashCards.footballQuarterback": "QB",
    "flashCards.footballEndZone": "ZONA DE ANOTACIÓN",
    "flashCards.footballRush": "Presión",
    "flashCards.footballNextPlay": "Siguiente jugada",
    "flashCards.footballHint": "Toca a un receptor o pulsa del 1 al 4 para lanzar. Lanza antes de que llegue la presión.",
    "flashCards.footballCaughtTemplate": "Atrapado para {n} yardas.",
    "flashCards.footballCaughtOne": "Atrapado para 1 yarda.",
    "flashCards.footballFirstDown": "Primer intento.",
    "flashCards.footballTouchdown": "¡Touchdown!",
    "flashCards.footballIncompleteTemplate": "Pase incompleto. {front} = {answer}.",
    "flashCards.footballSackedTemplate": "Derribado con pérdida de {n} yardas. {front} = {answer}.",
    "flashCards.footballSackedOneTemplate": "Derribado con pérdida de 1 yarda. {front} = {answer}.",
    "flashCards.footballSackedNoLossTemplate": "Derribado. {front} = {answer}.",
    "flashCards.footballTurnover": "Pérdida de balón por intentos.",
    "flashCards.footballFinal": "Final del partido",
    "flashCards.footballPointsCaption": "puntos",
    "flashCards.footballCountsTemplate": "Touchdowns {touchdowns} · Pases atrapados {completions} de {attempts} · Derribos {sacks}",
    "flashCards.footballYouThrewTemplate": "lanzaste al {n}",
    "flashCards.footballSackedLower": "derribado",
    "flashCards.footballChangeDefense": "Cambiar la defensa o las operaciones",
    // Math Runner (ADR 0124).
    "flashCards.runnerHowToPlay": "Cada fila de puertas es una operación. Tres carriles vienen hacia ti y cada puerta lleva un número. Solo uno es la respuesta. Ve a su carril con las flechas, las teclas del 1 al 3, un toque o un deslizamiento, y elígelo antes de que la fila llegue al corredor. Tienes tres corazones. El corredor va más rápido cuando pasas puertas y más lento cuando tropiezas.",
    "flashCards.runnerWhichFacts": "¿Qué operaciones?",
    "flashCards.runnerChoosePace": "Elige un ritmo para empezar",
    "flashCards.runnerPaceJog": "Trote",
    "flashCards.runnerPaceRun": "Carrera",
    "flashCards.runnerPaceSprint": "Esprint",
    "flashCards.runnerPaceSecondsTemplate": "{n} segundos al empezar",
    "flashCards.runnerScoreTemplate": "Puntos {n}",
    "flashCards.runnerHeartsAriaTemplate": "Corazones que quedan: {n}",
    "flashCards.runnerTrackAria": "La pista. Tres carriles de puertas vienen hacia el corredor. Elige el carril con la respuesta.",
    "flashCards.runnerLaneAriaTemplate": "Carril {n}: {value}",
    "flashCards.runnerLaneHiddenAriaTemplate": "Carril {n}",
    "flashCards.runnerAnnounceTemplate": "{front}. Carril 1: {a}. Carril 2: {b}. Carril 3: {c}.",
    "flashCards.runnerHint": "Toca o desliza hacia un carril, o usa las flechas, del 1 al 3 o la barra espaciadora. Elige un carril en cada fila: una fila sin elegir es un tropiezo.",
    "flashCards.runnerPassed": "¡Puerta superada!",
    "flashCards.runnerStumbledTemplate": "Tropezaste. {front} = {answer}.",
    "flashCards.runnerNoPickTemplate": "Tropezaste: no elegiste carril. {front} = {answer}.",
    "flashCards.runnerFinal": "Fin de la carrera",
    "flashCards.runnerScoreCaption": "puertas superadas",
    "flashCards.runnerCountsTemplate": "Puertas equivocadas {wrong} · Sin elegir carril {late}",
    "flashCards.runnerYouPickedTemplate": "elegiste {n}",
    "flashCards.runnerNoPickLower": "sin elegir carril",
    "flashCards.runnerChangePace": "Cambiar el ritmo o las operaciones",
    // Factor Merge (ADR 0128).
    "flashCards.mergeHowToPlay": "Cada tarjeta es un objetivo. Toca una ficha y luego otra para multiplicarlas: la segunda ficha se convierte en el producto. Una unión solo vale si el producto es un factor de alguno de los objetivos. Cuando una ficha es igual a un objetivo, el objetivo se cumple y sale otro nuevo. Siguen llegando fichas, y la partida termina cuando no queda ninguna unión posible. Tres veces por partida puedes quitar una ficha.",
    "flashCards.mergeLevelsLabel": "Nivel",
    "flashCards.mergeLevelG3": "Grado 3",
    "flashCards.mergeLevelG4": "Grado 4",
    "flashCards.mergeLevelG5": "Grado 5",
    "flashCards.mergeLevelG6": "Grado 6",
    "flashCards.mergeLevelG7": "Grado 7",
    "flashCards.mergeLevelG8": "Grado 8",
    "flashCards.mergeBoardAria": "El tablero. Toca una ficha y luego otra para unirlas. Las flechas se mueven entre fichas, y la barra espaciadora o Intro elige una.",
    "flashCards.mergeTargetsLabel": "Objetivos",
    "flashCards.mergeTargetMake": "Forma {n}",
    "flashCards.mergeTargetLcm": "m. c. m. de {a} y {b}",
    "flashCards.mergeTargetLcm3": "m. c. m. de {a}, {b} y {c}",
    "flashCards.mergeSpokenPower": "{base} elevado a {exponent}",
    "flashCards.mergeSpokenSquared": "{base} al cuadrado",
    "flashCards.mergeSpokenCubed": "{base} al cubo",
    "flashCards.mergeSpokenTimes": " por ",
    "flashCards.mergeSpokenEquals": "{powers} es igual a {value}",
    "flashCards.mergeTargetGcf": "m. c. d. de {a} y {b}",
    "flashCards.mergeStatusTemplate": "Objetivos cumplidos: {score} · Uniones rechazadas: {refused}",
    "flashCards.mergeSelectedTemplate": "Ficha {n} elegida. Toca otra ficha para unirlas, o toca {n} otra vez para soltarla.",
    "flashCards.mergeMergedTemplate": "{a} × {b} = {product}.",
    "flashCards.mergeRefusedTemplate": "{a} × {b} = {product}, y {product} no es un factor de {list}.",
    "flashCards.mergeRefusedAnyTemplate": "{a} × {b} = {product}, que no es un factor de ningún objetivo.",
    "flashCards.mergeCalmerPrefix": "¿Prefieres practicar las operaciones de una en una? Prueba el ",
    "flashCards.mergeCalmerLink": "cuestionario de multiplicación",
    "flashCards.mergeCalmerSuffix": ".",
    "flashCards.mergeListTwo": "{a} o {b}",
    "flashCards.mergeListThree": "{a}, {b} o {c}",
    "flashCards.mergeMadeTemplate": "Objetivo cumplido: {line}.",
    "flashCards.mergeMadeLcmTemplate": "Objetivo cumplido: el m. c. m. de {a} y {b} es {value}. {line}.",
    "flashCards.mergeMadeLcm3Template": "Objetivo cumplido: el m. c. m. de {a}, {b} y {c} es {value}. {line}.",
    "flashCards.mergeMadeGcfTemplate": "Objetivo cumplido: el m. c. d. de {a} y {b} es {value}. {line}.",
    "flashCards.mergeClearedTemplate": "Quitaste {n}.",
    "flashCards.mergeClearOn": "Modo quitar: toca una ficha para quitarla. Pulsa el botón otra vez, o Esc, para cancelar.",
    "flashCards.mergeClearOff": "Quitar cancelado.",
    "flashCards.mergeClearButtonTemplate": "Quitar una ficha (quedan {n})",
    "flashCards.mergeOver": "No queda ninguna unión posible. La partida terminó.",
    "flashCards.mergeFinal": "Fin de la partida",
    "flashCards.mergeScoreCaption": "objetivos cumplidos",
    "flashCards.mergeSummaryRefusedTemplate": "Uniones rechazadas: {n}",
    "flashCards.mergePracticeHeading": "Practica fuera del juego",
    // Shop Rush (ADR 0131).
    "flashCards.shopHowToPlay": "Diez clientes llegan al mostrador, uno por uno. Prepara cada pedido y pulsa Atender. Acertar a la primera vale 2 estrellas, acertar al segundo intento vale 1, y un cliente que recibe dos respuestas equivocadas se marcha. En un turno puedes ganar 20 estrellas.",
    "flashCards.shopLevelsLabel": "Nivel",
    "flashCards.shopLevelG2": "Grado 2",
    "flashCards.shopLevelG3": "Grado 3",
    "flashCards.shopLevelG4": "Grado 4",
    "flashCards.shopLevelG5": "Grado 5",
    "flashCards.shopLevelG6": "Grado 6",
    "flashCards.shopLevelG7": "Grado 7",
    "flashCards.shopLevelG8": "Grado 8",
    "flashCards.shopLevelNoteG2": "Contar monedas, dar el cambio de $1.00, sumar precios y fracciones de una pizza.",
    "flashCards.shopLevelNoteG3": "Cambio de $1, $2 y $5, sumar precios, tablas de multiplicar y fracciones de pizza.",
    "flashCards.shopLevelNoteG4": "Cambio hasta $10, cuentas más largas, un dígito por dos y fracciones de pizza.",
    "flashCards.shopLevelNoteG5": "Cambio hasta $20, cuentas con centavos, dos dígitos por dos y fracciones de pizza.",
    "flashCards.shopLevelNoteG6": "Descuentos y propinas con porcentajes fáciles, precio unitario y la mejor compra.",
    "flashCards.shopLevelNoteG7": "Descuentos, impuesto sobre las ventas, propinas y recargos.",
    "flashCards.shopLevelNoteG8": "Descuento y luego impuesto, precio original, cambio porcentual e interés simple.",
    "flashCards.shopPacesLabel": "Ritmo",
    "flashCards.shopPaceCalm": "Calma",
    "flashCards.shopPaceBusy": "Ajetreo",
    "flashCards.shopPaceRush": "Prisa",
    "flashCards.shopPaceNoClock": "Sin reloj",
    "flashCards.shopPaceSecondsTemplate": "{n} segundos por cliente",
    "flashCards.shopStart": "Empezar el turno",
    "flashCards.shopCalmerPrefix": "¿Prefieres practicar las sumas de una en una? Prueba el ",
    "flashCards.shopCalmerLink": "cuestionario de suma",
    "flashCards.shopCalmerSuffix": ".",
    "flashCards.shopStatusTemplate": "Cliente {n} de {total} · Estrellas: {stars} de {max}",
    "flashCards.shopCounterLabel": "El mostrador",
    "flashCards.shopPatienceLabel": "Paciencia",
    "flashCards.shopPause": "Pausa",
    "flashCards.shopResume": "Reanudar",
    "flashCards.shopPausedNote": "En pausa. Pulsa Reanudar para seguir.",
    "flashCards.shopServe": "Atender",
    "flashCards.shopNextCustomer": "Siguiente cliente",
    "flashCards.shopFinish": "Terminar el turno",
    "flashCards.shopUndo": "Deshacer",
    "flashCards.shopClear": "Borrar todo",
    "flashCards.shopBackspace": "Borrar",
    "flashCards.shopGivenTemplate": "Entregado: {amount}",
    "flashCards.shopAddTemplate": "Añadir {amount}",
    "flashCards.shopTrayLabel": "Monedas y billetes",
    "flashCards.shopKeypadLabel": "Teclado de la caja",
    "flashCards.shopEntryLabel": "Tu respuesta",
    "flashCards.shopOffersLabel": "Las dos ofertas",
    "flashCards.shopOfferTemplate": "{count} por {price}",
    "flashCards.shopBoardLabel": "Tabla de precios",
    "flashCards.shopCoinsLabel": "Las monedas",
    "flashCards.shopArriveTemplate": "Cliente {n}. {order}",
    "flashCards.shopOrderChange": "Paga {paid} por un bocadillo de {price}. Da el cambio.",
    "flashCards.shopOrderTotal": "Cobra {lines}. ¿Cuál es el total?",
    "flashCards.shopOrderGroups": "{groups} {container} de {each} {thing}: ¿cuántas unidades hay en total?",
    "flashCards.shopOrderFraction": "{fraction} de una pizza cortada en {slices} porciones: ¿cuántas porciones?",
    "flashCards.shopOrderDiscountSale": "{rate}% de descuento sobre {price}: ¿cuál es el precio de oferta?",
    "flashCards.shopOrderDiscountOff": "{rate}% de descuento sobre {price}: ¿cuánto se descuenta?",
    "flashCards.shopOrderTax": "{price} más {rate}% de impuesto: ¿cuál es el total?",
    "flashCards.shopOrderTip": "Una propina del {rate}% sobre una cuenta de {price}: ¿cuánto es la propina?",
    "flashCards.shopOrderBetterBuy": "{a}, o {b}: ¿cuál sale más barato por unidad?",
    "flashCards.shopOrderCoins": "El cliente paga con estas monedas. ¿Cuánto dinero es?",
    "flashCards.shopOrderUnitPrice": "{count} {items} por {total}: ¿cuánto cuesta uno?",
    "flashCards.shopOrderMarkup": "La tienda pagó {cost} por el artículo y le suma un {rate}%. ¿Cuál es el precio de venta?",
    "flashCards.shopOrderDiscountTax": "{price}, {discount}% de descuento y luego {tax}% de impuesto: ¿cuál es el total?",
    "flashCards.shopOrderOriginalPrice": "Con {rate}% de descuento cuesta {sale}. ¿Cuál era el precio original?",
    "flashCards.shopOrderPercentUp": "El precio pasó de {from} a {to}. ¿Qué porcentaje subió?",
    "flashCards.shopOrderPercentDown": "El precio pasó de {from} a {to}. ¿Qué porcentaje bajó?",
    "flashCards.shopOrderInterest": "{principal} al {rate}% anual durante {years} años: ¿cuánto interés?",
    "flashCards.shopOrderInterestOne": "{principal} al {rate}% anual durante 1 año: ¿cuánto interés?",
    "flashCards.shopServedFirst": "¡Gracias! Dos estrellas.",
    "flashCards.shopServedSecond": "¡Gracias! Una estrella.",
    "flashCards.shopMissTemplate": "No exactamente. {hint} Inténtalo otra vez.",
    "flashCards.shopLeftWrongTemplate": "El cliente se marchó. {worked}",
    "flashCards.shopLeftPatienceTemplate": "Sin paciencia: el cliente se marchó. {worked}",
    "flashCards.shopItemApple": "manzana",
    "flashCards.shopItemApplePlural": "manzanas",
    "flashCards.shopItemBanana": "plátano",
    "flashCards.shopItemBananaPlural": "plátanos",
    "flashCards.shopItemCookie": "galleta",
    "flashCards.shopItemCookiePlural": "galletas",
    "flashCards.shopItemJuice": "caja de jugo",
    "flashCards.shopItemJuicePlural": "cajas de jugo",
    "flashCards.shopItemMilk": "cartón de leche",
    "flashCards.shopItemMilkPlural": "cartones de leche",
    "flashCards.shopItemBread": "barra de pan",
    "flashCards.shopItemBreadPlural": "barras de pan",
    "flashCards.shopItemSandwich": "sándwich",
    "flashCards.shopItemSandwichPlural": "sándwiches",
    "flashCards.shopItemNotebook": "cuaderno",
    "flashCards.shopItemNotebookPlural": "cuadernos",
    "flashCards.shopItemSticker": "pegatina",
    "flashCards.shopItemStickerPlural": "pegatinas",
    "flashCards.shopItemPencil": "lápiz",
    "flashCards.shopItemPencilPlural": "lápices",
    "flashCards.shopContainerBags": "bolsas",
    "flashCards.shopContainerBoxes": "cajas",
    "flashCards.shopContainerTrays": "bandejas",
    "flashCards.shopContainerBaskets": "cestas",
    "flashCards.shopContainerPacks": "paquetes",
    "flashCards.shopContainerJars": "frascos",
    "flashCards.shopContainerCrates": "cajones",
    "flashCards.shopContainerCartons": "cartones",
    "flashCards.shopThingRolls": "bollos",
    "flashCards.shopThingCrayons": "lápices de colores",
    "flashCards.shopThingMuffins": "magdalenas",
    "flashCards.shopThingApples": "manzanas",
    "flashCards.shopThingStickers": "pegatinas",
    "flashCards.shopThingCookies": "galletas",
    "flashCards.shopThingOranges": "naranjas",
    "flashCards.shopThingEggs": "huevos",
    "flashCards.shopHintChange": "Cuenta hacia arriba desde {price} hasta {paid}.",
    "flashCards.shopHintTotal": "Suma primero los dólares y luego los centavos.",
    "flashCards.shopHintGroups": "{groups} grupos de {each}.",
    "flashCards.shopHintFraction": "Reparte {slices} porciones en {den} partes iguales: {slices} ÷ {den} = {per}.",
    "flashCards.shopHintFractionSplit": "Reparte {slices} porciones en {den} partes iguales.",
    "flashCards.shopHintPercentOf": "Primero halla el {rate}% de {price}.",
    "flashCards.shopHintTenthBuild": "El 10% de {price} es {tenth}. Arma el {rate}% a partir de ahí.",
    "flashCards.shopHintTenthDivide": "El 10% es una décima parte: divide {price} entre 10.",
    "flashCards.shopHintTenth": "El 10% de {price} es {tenth}.",
    "flashCards.shopHintQuarter": "El 25% es un cuarto: divide {price} entre 4.",
    "flashCards.shopHintTip15": "El 10% de {price} es {tenth}, y el 5% es la mitad de eso.",
    "flashCards.shopHintTip18": "El 10% de {price} es {tenth}. Halla el 20% y réstale el 2%.",
    "flashCards.shopHintBetterBuy": "Halla el precio de uno en cada oferta.",
    "flashCards.shopHintCoins": "Cuenta primero las monedas de 25¢, luego las de 10¢, las de 5¢ y las de 1¢.",
    "flashCards.shopHintTotalCents": "Suma los precios de uno en uno.",
    "flashCards.shopHintFractionSlice": "Cada porción es 1/{den} de la pizza. Cuenta cuántas porciones forman {fraction}.",
    "flashCards.shopHintUnitPrice": "Reparte {total} en partes iguales entre {count}.",
    "flashCards.shopHintDiscountTax": "Primero quita el descuento y luego suma el impuesto sobre el precio de oferta.",
    "flashCards.shopHintOriginalPrice": "Con {rate}% de descuento, {sale} es el {rest}% del precio.",
    "flashCards.shopHintPercentChange": "Halla el cambio y luego divídelo entre el precio inicial.",
    "flashCards.shopHintInterest": "El interés de un año es el {rate}% de {principal}.",
    "flashCards.shopWorkedChange": "{steps}, así que el cambio es {change}.",
    "flashCards.shopWorkedTotal": "{terms} = {total}.",
    "flashCards.shopWorkedGroups": "{groups} × {each} = {answer}.",
    "flashCards.shopWorkedFraction": "{fraction} de {slices}: {slices} ÷ {den} = {per}, y luego {per} × {num} = {answer}.",
    "flashCards.shopWorkedDiscountSale": "El {rate}% de {price} es {off}; {price} − {off} = {sale}.",
    "flashCards.shopWorkedDiscountOff": "El {rate}% de {price} es {off}.",
    "flashCards.shopWorkedTax": "El {rate}% de {price} es {tax}; {price} + {tax} = {total}.",
    "flashCards.shopWorkedTip10": "10% de {bill} = {bill} ÷ 10 = {tip}.",
    "flashCards.shopWorkedTip15": "10% = {tenth} y 5% = {half}, así que 15% = {tenth} + {half} = {tip}.",
    "flashCards.shopWorkedTip18": "20% = {twenty} y 2% = {two}, así que 18% = {twenty} − {two} = {tip}.",
    "flashCards.shopWorkedTip20": "10% = {tenth}, así que 20% = {tenth} × 2 = {tip}.",
    "flashCards.shopWorkedTip25": "25% de {bill} = {bill} ÷ 4 = {tip}.",
    "flashCards.shopWorkedBetterBuy": "{priceA} ÷ {countA} = {unitA}, y {priceB} ÷ {countB} = {unitB}, así que {winnerCount} por {winnerPrice}.",
    "flashCards.shopWorkedCoins": "{terms} = {total}.",
    "flashCards.shopWorkedUnitPrice": "{total} ÷ {count} = {unit}.",
    "flashCards.shopWorkedDiscountTax": "El {discount}% de {price} es {off}, así que el precio de oferta es {sale}; el {tax}% de {sale} es {taxAmount}; {sale} + {taxAmount} = {total}.",
    "flashCards.shopWorkedOriginalPrice": "El {rest}% es {sale}, así que el 10% es {tenth} y el 100% es {original}.",
    "flashCards.shopWorkedPercentChange": "{big} − {small} = {diff}; {diff} ÷ {from} = {ratio}, así que {percent}%.",
    "flashCards.shopWorkedInterest": "El {rate}% de {principal} es {yearly} al año; {yearly} × {years} = {interest}.",
    "flashCards.shopWorkedInterestOne": "El {rate}% de {principal} es {interest}.",
    "flashCards.shopFinal": "Turno terminado",
    "flashCards.shopScoreCaptionTemplate": "estrellas de {max}",
    "flashCards.shopSummaryServedTemplate": "Pedidos atendidos: {n} de {total}",
    "flashCards.shopSummaryWrongTemplate": "Intentos fallidos: {n}",
    "flashCards.shopSummaryWalkedTemplate": "Clientes que se quedaron sin paciencia: {n}",
    "flashCards.shopPracticeHeading": "Practica fuera del juego",
    // Hidden Picture (ADR 0133).
    "flashCards.pictureHowToPlay": "Hay una imagen escondida bajo 25 casillas, y cada casilla es una operación. Toca una casilla, escribe la respuesta y pulsa Comprobar. Si aciertas, la casilla se levanta. Si fallas, tienes un segundo intento, y tras el segundo fallo se muestra la respuesta y la casilla se levanta igualmente.",
    "flashCards.pictureWhichFacts": "¿Qué operaciones?",
    "flashCards.pictureStart": "Empezar",
    "flashCards.pictureHintSnowman": "Algo para el invierno se esconde bajo las casillas.",
    "flashCards.pictureHintHeart": "Algo para San Valentín se esconde bajo las casillas.",
    "flashCards.pictureHintShamrock": "Algo para el Día de San Patricio se esconde bajo las casillas.",
    "flashCards.pictureHintTulip": "Algo para la primavera se esconde bajo las casillas.",
    "flashCards.pictureHintIcecream": "Algo para el verano se esconde bajo las casillas.",
    "flashCards.pictureHintApple": "Algo para el otoño y la vuelta al cole se esconde bajo las casillas.",
    "flashCards.pictureHintPumpkin": "Algo para Halloween se esconde bajo las casillas.",
    "flashCards.pictureHintTurkey": "Algo para Acción de Gracias se esconde bajo las casillas.",
    "flashCards.pictureHintGift": "Algo para las fiestas de invierno se esconde bajo las casillas.",
    "flashCards.pictureCaptionSnowman": "¡Un muñeco de nieve para el invierno!",
    "flashCards.pictureCaptionHeart": "¡Un corazón para San Valentín!",
    "flashCards.pictureCaptionShamrock": "¡Un trébol para el Día de San Patricio!",
    "flashCards.pictureCaptionTulip": "¡Un tulipán para la primavera!",
    "flashCards.pictureCaptionIcecream": "¡Un cucurucho de helado para el verano!",
    "flashCards.pictureCaptionApple": "¡Una manzana para la vuelta al cole!",
    "flashCards.pictureCaptionPumpkin": "¡Una calabaza con cara para Halloween!",
    "flashCards.pictureCaptionTurkey": "¡Un pavo para Acción de Gracias!",
    "flashCards.pictureCaptionGift": "¡Un regalo envuelto para las fiestas de invierno!",
    "flashCards.pictureLeftTemplate": "Casillas por levantar: {n}",
    "flashCards.pictureRightTemplate": "Acertadas a la primera: {n}",
    "flashCards.pictureBoardLabel": "Casillas. Elige una para responder.",
    "flashCards.pictureTileAriaTemplate": "Fila {row}, casilla {col}: {fact}",
    "flashCards.pictureCheck": "Comprobar",
    "flashCards.pictureRight": "¡Correcto!",
    "flashCards.pictureTryAgain": "Casi. Inténtalo otra vez.",
    "flashCards.pictureAnswerShownTemplate": "La respuesta es {fact} = {answer}.",
    "flashCards.pictureAnnounceRightTemplate": "Correcto. Casillas por levantar: {n}.",
    "flashCards.pictureAnnounceWrongTemplate": "Casi. Inténtalo otra vez: {fact}.",
    "flashCards.pictureAnnounceMissedTemplate": "La respuesta es {fact} = {answer}. Casillas por levantar: {n}.",
    "flashCards.pictureAnnounceNextTemplate": "Siguiente: {fact}.",
    "flashCards.pictureAnnounceDone": "La imagen está completa.",
    "flashCards.pictureHint": "Toca cualquier casilla para responder esa en su lugar. Pulsa Enter para comprobar.",
    "flashCards.pictureFinal": "La imagen está completa",
    "flashCards.pictureScoreTemplate": "{n} de {total} casillas acertadas a la primera",
    "flashCards.pictureSummaryWrongTemplate": "Intentos fallidos: {n}",
    "flashCards.pictureMoreHeading": "Sigue practicando",
    // Slingshot Plate Smash (ADR 0060).
    "flashCards.platesHowToPlay": "Lee la operación del cartel y rompe el plato con la respuesta. Arrastra hacia atrás en la caseta y suelta para disparar. Tienes 60 segundos: 10 puntos por acertar al primer tiro, y el doble a partir de tres seguidos.",
    "flashCards.platesWhichFacts": "¿Qué operaciones?",
    "flashCards.platesChooseMode": "Elige una caseta para empezar",
    "flashCards.platesEasy": "Fácil",
    "flashCards.platesHard": "Difícil",
    "flashCards.platesEasyHint": "Los platos están quietos",
    "flashCards.platesHardHint": "Los platos van en una cinta",
    "flashCards.platesBoothAria": "Caseta del tirachinas. Arrastra hacia atrás y suelta para disparar, o apunta con las flechas y dispara con la barra espaciadora.",
    "flashCards.platesHint": "Arrastra hacia atrás y suelta para disparar. Las flechas apuntan y la barra espaciadora dispara.",
    "flashCards.platesAimingAtTemplate": "Apuntando al {n}",
    "flashCards.platesAimingAtNothing": "Apuntando entre los platos",
    "flashCards.platesHotStreak": "¡Racha! ×2",
    "flashCards.platesVerdictHitTemplate": "¡Roto! +{n}",
    "flashCards.platesVerdictLateTemplate": "Ahí está: {n}. Sin puntos después de un plato equivocado.",
    "flashCards.platesVerdictWrongTemplate": "Ese plato era {n}. ¡Otra vez!",
    "flashCards.platesVerdictMiss": "No has dado a ningún plato",
    "flashCards.platesTimeUp": "¡Se acabó el tiempo!",
    "flashCards.platesPointsCaption": "puntos",
    "flashCards.platesCountsTemplate": "{hits} rotos al primer tiro · {wrong} platos equivocados",
    "flashCards.platesMadeTheBoard": "¡Has entrado en la clasificación!",
    "flashCards.platesNameLabel": "Tu nombre para la clasificación",
    "flashCards.platesNamePlaceholder": "Nombre o apodo",
    "flashCards.platesSave": "Guardar",
    "flashCards.platesSkip": "No guardar esta puntuación",
    "flashCards.platesRankTemplate": "¡Estás en el puesto {n}!",
    "flashCards.platesYouHitTemplate": "rompiste el {n}",
    "flashCards.platesChangeMode": "Elegir otra caseta",
    "flashCards.platesLeaderboard": "Clasificación",
    "flashCards.platesBoardEmpty": "Aún no hay puntuaciones. ¡Sé el primero!",
    "flashCards.platesBoardName": "Nombre",
    "flashCards.platesBoardScore": "Puntos",
    "flashCards.platesBoardFacts": "Operaciones",
    "flashCards.platesBoardNote": "Solo se guarda en este navegador. No se envía a ningún sitio.",
    "flashCards.platesClear": "Borrar la clasificación",
    "flashCards.platesClearConfirm": "¿Borrar todas las puntuaciones y nombres?",
    "flashCards.platesClearYes": "Borrar",
    "flashCards.platesClearNo": "Conservar",
    // The Baker Street Math Crossword (ADR 0134).
    "flashCards.crosswordHowToPlay": "Cada línea de la cuadrícula es una frase numérica, que se lee de izquierda a derecha o de arriba abajo, y casi todos los números están en dos líneas a la vez. Completa los huecos para que todas las líneas sean verdaderas. Resuelve cinco casos para cerrar el expediente. Cada caso vale hasta 3 lupas: Preguntar a Holmes y una Comprobación que encuentra un error cuestan una cada una, y un caso resuelto siempre da al menos una.",
    "flashCards.crosswordRanksLabel": "Rango",
    "flashCards.crosswordRankConstable": "Agente",
    "flashCards.crosswordRankInspector": "Inspector",
    "flashCards.crosswordRankDetective": "Detective",
    "flashCards.crosswordRankGradesConstable": "1.º–2.º",
    "flashCards.crosswordRankGradesInspector": "3.º–4.º",
    "flashCards.crosswordRankGradesDetective": "5.º–6.º",
    "flashCards.crosswordRankNoteConstable": "Sumas y restas dentro del 20, en una cuadrícula de nueve números. En el escritorio hay un banco con los números que faltan.",
    "flashCards.crosswordRankNoteInspector": "Sumas y restas hasta 100, o multiplicaciones y divisiones de las tablas hasta 10 × 10, en una cuadrícula de nueve números. En el escritorio hay un banco con los números que faltan.",
    "flashCards.crosswordRankNoteDetective": "Una cuadrícula más grande con tres números en cada línea, así que la multiplicación y la división van antes que la suma y la resta. Sin banco de números.",
    "flashCards.crosswordStart": "Abrir el expediente",
    "flashCards.crosswordCalmerPrefix": "¿Prefieres practicar las sumas de una en una? Prueba el ",
    "flashCards.crosswordCalmerLink": "cuestionario de suma",
    "flashCards.crosswordCalmerSuffix": ".",
    "flashCards.crosswordPlateStreet": "Baker Street",
    "flashCards.crosswordStatusTemplate": "Caso {n} de {total} · Lupas: {glasses} de {max}",
    "flashCards.crosswordWorthTemplate": "Este caso: {n} de {max}",
    "flashCards.crosswordBoardLabel": "El crucigrama",
    "flashCards.crosswordLinesLabel": "Las líneas",
    "flashCards.crosswordBankLabel": "Números que faltan. Elige uno para ponerlo en el hueco seleccionado.",
    "flashCards.crosswordCheck": "Comprobar",
    "flashCards.crosswordAsk": "Preguntar a Holmes",
    "flashCards.crosswordNextCase": "Siguiente caso",
    "flashCards.crosswordFinish": "Cerrar el expediente",
    "flashCards.crosswordArriveTemplate": "Caso {n}. {title}. {brief}",
    "flashCards.crosswordSolvedTemplate": "Elemental. Caso cerrado con {n} de {max} lupas.",
    "flashCards.crosswordCheckWrongOne": "Un número no es correcto. Está marcado. Eso cuesta una lupa.",
    "flashCards.crosswordCheckWrongMany": "{n} números no son correctos. Están marcados. Eso cuesta una lupa.",
    "flashCards.crosswordCheckRight": "Todo lo que has puesto hasta ahora es correcto.",
    "flashCards.crosswordCheckEmpty": "Escribe primero un número y luego comprueba.",
    "flashCards.crosswordHintTemplate": "{line} dice {open}. Solo sirve {value}: {closed}.",
    "flashCards.crosswordLineAcross": "Horizontal {n}",
    "flashCards.crosswordLineDown": "Vertical {n}",
    "flashCards.crosswordTagAcross": "H{n}",
    "flashCards.crosswordTagDown": "V{n}",
    "flashCards.crosswordPositionFirst": "primer número",
    "flashCards.crosswordPositionSecond": "segundo número",
    "flashCards.crosswordPositionThird": "tercer número",
    "flashCards.crosswordPositionAnswer": "resultado",
    "flashCards.crosswordCellLabelTemplate": "{line}, {position}",
    "flashCards.crosswordLineReadingTemplate": "{line}: {sentence}",
    "flashCards.crosswordBlankWord": "hueco",
    "flashCards.crosswordFinal": "Caso cerrado",
    "flashCards.crosswordScoreCaptionTemplate": "lupas de {max}",
    "flashCards.crosswordSummaryUnaidedTemplate": "Casos resueltos sin preguntar a Holmes: {n} de {total}",
    "flashCards.crosswordSummaryWrongTemplate": "Números incorrectos que encontró la comprobación: {n}",
    "flashCards.crosswordSummaryHolmesTemplate": "Números que puso Holmes: {n}",
    "flashCards.crosswordPracticeHeading": "Practica fuera del juego",
    "flashCards.crosswordCase1Title": "El caso del libro de cuentas borroso",
    "flashCards.crosswordCase1Brief": "Las cuentas de la señora Hudson se han mojado con la lluvia. Calcula las cifras que se ha llevado la humedad.",
    "flashCards.crosswordCase2Title": "El caso del telegrama de medianoche",
    "flashCards.crosswordCase2Brief": "Un telegrama llegó a Baker Street a medianoche, pero le faltan algunas cifras rasgadas.",
    "flashCards.crosswordCase3Title": "El caso del relojero silencioso",
    "flashCards.crosswordCase3Brief": "Se encontró la lista de precios del relojero sobre su mesa, con algunos números borrados.",
    "flashCards.crosswordCase4Title": "El caso del muelle con niebla",
    "flashCards.crosswordCase4Brief": "La niebla ha cubierto una lista de carga del muelle del río y ha escondido algunas cifras.",
    "flashCards.crosswordCase5Title": "El caso de la caja cerrada con llave",
    "flashCards.crosswordCase5Brief": "El inspector Lestrade ha abierto la caja, pero a su hoja de cuentas le faltan algunos números.",
    "flashCards.crosswordCase6Title": "El caso del paraguas olvidado",
    "flashCards.crosswordCase6Brief": "Un paraguas volvió a Baker Street con una hoja de cifras atada, y la mitad están manchadas.",
    "flashCards.crosswordCase7Title": "El caso del farol inclinado",
    "flashCards.crosswordCase7Brief": "El farolero anotó su ronda, pero la luz de gas ha desteñido algunos números.",
    "flashCards.crosswordCase8Title": "El caso de la curiosa lata de té",
    "flashCards.crosswordCase8Brief": "La señora Hudson encontró una suma dentro de una lata de té vieja, y algunos de sus números están borrosos.",
    "flashCards.crosswordCase9Title": "El caso de la escalera susurrante",
    "flashCards.crosswordCase9Brief": "Alguien deslizó una hoja de sumas bajo la puerta del 221B, y le faltan algunos números.",
    "flashCards.crosswordCase10Title": "El caso del matasellos desvaído",
    "flashCards.crosswordCase10Brief": "Los paquetes de correos tienen las etiquetas desteñidas. Completa las cifras para que todas las líneas sean verdaderas.",
    "flashCards.crosswordCase11Title": "El caso del cochero errante",
    "flashCards.crosswordCase11Brief": "Un cochero llevaba la cuenta de sus carreras, y la lluvia ha borrado una parte.",
    "flashCards.crosswordCase12Title": "El caso del violín prestado",
    "flashCards.crosswordCase12Brief": "El doctor Watson encontró una hoja de cifras en el estuche del violín. Ayuda a Holmes a leerla.",
} satisfies Record<FlashCardsStringKey, string>;

const fr = {
    "flashCards.ariaFlippedTemplate": "{front} égale {back}. Touchez pour masquer la réponse.",
    "flashCards.ariaUnflippedTemplate": "{front}. Touchez pour afficher la réponse.",
    "flashCards.tapToFlip": "Touchez pour retourner",
    "flashCards.emptyDeck":
        "Choisissez une opération et au moins un nombre pour commencer un jeu de cartes.",
    "flashCards.cardProgress": "Carte {current} sur {total}",
    "flashCards.previous": "Précédent",
    "flashCards.next": "Suivant",
    "flashCards.newDeck": "Nouveau jeu de cartes",
    "flashCards.swipeHint":
        "Balayez ou utilisez les flèches pour changer de carte, espace pour la retourner.",
    "flashCards.changeWhatToPractice": "Changer ce que l'on pratique",
    "flashCards.frequentlyAskedQuestions": "Questions fréquentes",
    "flashCards.printTheseCards": "Imprimer ces cartes mémo",
    "flashCards.cards": "cartes",
    "flashCards.printIntroPrefix":
        "{count} cartes par feuille, au format d'une fiche bristol. Découpez le long des pointillés. ",
    "flashCards.printIntroDoubleSided":
        "Imprimez en recto verso, en retournant sur le grand côté, et chaque réponse se retrouve au dos de sa propre carte.",
    "flashCards.printIntroSingleSided":
        "Les réponses au dos sont désactivées, donc les cartes n'impriment que le problème.",
    "flashCards.sheetsOfCardsLabel": "Feuilles de cartes : ",
    "flashCards.addSheetAria": "Ajouter une feuille de cartes",
    "flashCards.removeSheetAria": "Supprimer une feuille de cartes",
    "flashCards.answersOnBackLabel": "Réponses au dos : ",
    "flashCards.withAnswerBacks": ", avec réponses au dos",
    "flashCards.onSheetsSuffix": " sur {n} {sheetWord}",
    "flashCards.sheet": "feuille",
    "flashCards.sheets": "feuilles",
    "flashCards.cappedByPoolNote":
        "Chaque carte est différente, et ces réglages ne créent que {count} {factWord} de " +
        "{operation} différentes — c'est tout l'ensemble. Élargissez la plage de nombres pour en avoir plus.",
    "flashCards.fact": "opération",
    "flashCards.facts": "opérations",
    "flashCards.chooseOperationNote":
        "Choisissez une opération et au moins un nombre pour imprimer un jeu de cartes.",
    "flashCards.loadingPreview": "Chargement de l'aperçu…",
    "flashCards.openFlashCardsPdf": "Ouvrir le PDF des cartes mémo",
    "flashCards.buildingCards": "Préparation de vos cartes…",
    "flashCards.previewOfFlashCardsPage1": "Aperçu de la page 1 des cartes",
    "flashCards.missedFactsCountTemplate": "{count} {factWord} à revoir.",
    "flashCards.printForPractice": "Imprimer pour s'entraîner",
    "flashCards.loadingPracticeSheet": "Chargement de la fiche d'entraînement…",
    "flashCards.quizEmptyNote":
        "Choisissez une opération et au moins un nombre ci-dessous pour commencer un quiz.",
    "flashCards.chooseDifferentLength": "Choisir une autre durée",
    "flashCards.howLongDoYouWant": "Combien de temps voulez-vous ?",
    "flashCards.noClockJustPractise": "Sans chronomètre — juste pour s'entraîner",
    "flashCards.changeWhatToPractise": "Changer ce que l'on pratique",
    "flashCards.thirtySeconds": "30 secondes",
    "flashCards.oneMinute": "1 minute",
    "flashCards.twoMinutes": "2 minutes",
    "flashCards.threeMinutes": "3 minutes",
    "flashCards.timeLeftAria": "Temps restant",
    "flashCards.scoreTemplate": "Score {n}",
    "flashCards.streakTemplate": "Série {n}",
    "flashCards.getReady": "Préparez-vous…",
    "flashCards.answerForAriaTemplate": "Réponse pour {front}",
    "flashCards.enterButton": "Entrée",
    "flashCards.correct": "Correct",
    "flashCards.skipped": "Passé",
    "flashCards.notQuite": "Pas tout à fait",
    "flashCards.finishButton": "Terminer",
    "flashCards.quizHint":
        "Saisissez la réponse et appuyez sur Entrée. Appuyez sur Entrée avec la case vide pour passer.",
    "flashCards.noClockHintSuffix": " Il n'y a pas de chronomètre — arrêtez quand vous voulez.",
    "flashCards.correctCaption": "bonnes réponses",
    "flashCards.againstTheClockSuffix": " contre la montre",
    "flashCards.answeredCountsTemplate":
        "{answered} répondues · {wrong} fausses · {skipped} passées",
    "flashCards.newBest": "Nouveau record !",
    "flashCards.yourBestTemplate": "Votre record : {n}",
    "flashCards.worthAnotherLookHeading": "À revoir",
    "flashCards.skippedLower": "passé",
    "flashCards.youPutTemplate": "vous avez mis {typed}",
    "flashCards.playAgain": "Rejouer",
    "flashCards.gameEmptyNote":
        "Choisissez une opération et au moins un nombre ci-dessous pour commencer le jeu.",
    "flashCards.gameHowToPlay":
        "Les calculs tombent du haut de l'écran. Tapez la réponse et appuyez sur Entrée pour en effacer un avant qu'il touche le sol. Au troisième qui touche le sol, la partie est finie.",
    "flashCards.gameStart": "Commencer la partie",
    "flashCards.gameCalmerPrefix": "Vous préférez vous entraîner sans vous presser ? Essayez le ",
    "flashCards.gameCalmerLink": "quiz sans chrono",
    "flashCards.gameCalmerSuffix": ".",
    "flashCards.gameLevelTemplate": "Niveau {n}",
    "flashCards.gameLivesAriaTemplate": "Il vous reste {n} vies",
    "flashCards.gameOneLifeAria": "Il vous reste 1 vie",
    "flashCards.gameLastLife": "Un calcul a touché le sol. Il vous reste une vie.",
    "flashCards.gamePause": "Pause",
    "flashCards.gameResume": "Reprendre",
    "flashCards.gamePaused": "En pause",
    "flashCards.gamePausedHint": "Les calculs sont masqués jusqu'à ce que vous repreniez.",
    "flashCards.gameAnswerAria": "Tapez une réponse pour effacer un calcul qui tombe",
    "flashCards.gameHint": "Tapez la réponse et appuyez sur Entrée. Une erreur ne coûte que du temps.",
    "flashCards.gameLifeLostTemplate": "Un calcul a touché le sol. Il vous reste {n} vies.",
    "flashCards.gameLevelUpTemplate": "Niveau {n}. Ils tombent plus vite maintenant.",
    "flashCards.gameOver": "Partie terminée",
    "flashCards.gameClearedCaption": "calculs effacés",
    "flashCards.gameReachedTemplate": "Niveau {level} atteint · {wrong} erreurs",
    "flashCards.dropChangeFacts": "Choisir d'autres calculs",
    "flashCards.chartIntro": "Répondez à un calcul pour remplir sa case, et son reflet de l'autre côté de la diagonale. Touchez une case vide pour la choisir vous-même. Votre table est enregistrée sur cet appareil.",
    "flashCards.chartProgressTemplate": "{filled} calculs sur {total}",
    "flashCards.chartHint": "Tapez la réponse et appuyez sur Entrée. Entrée avec la case vide passe à une autre case.",
    "flashCards.chartStartOver": "Commencer une nouvelle table",
    "flashCards.chartConfirmStartOver": "Effacer toute votre table ?",
    "flashCards.chartConfirmYes": "Oui, l'effacer",
    "flashCards.chartConfirmNo": "La garder",
    "flashCards.chartComplete": "Vous avez rempli toute la table !",
    "flashCards.chartCompleteDetailTemplate": "Les {total} calculs · {wrong} erreurs en chemin",
    "flashCards.chartGridCaption": "Votre table de multiplication",
    "flashCards.chartCellEmptyAriaTemplate": "{a} fois {b} : vide. Poser celle-ci.",
    "flashCards.chartCellTrickyAriaTemplate": "{a} fois {b} : difficile. Poser celle-ci.",
    "flashCards.chartCellFilledAriaTemplate": "{a} fois {b} : {answer}",
    "flashCards.chartWhichChart": "Quelle table ?",
    "flashCards.chartPickAddition": "Addition",
    "flashCards.chartPickMultiplication": "Multiplication",
    "flashCards.chartGridCaptionAddition": "Votre table d'addition",
    "flashCards.chartCellEmptyAdditionAriaTemplate": "{a} plus {b} : vide. Poser celle-ci.",
    "flashCards.chartCellTrickyAdditionAriaTemplate": "{a} plus {b} : difficile. Poser celle-ci.",
    "flashCards.chartCellFilledAdditionAriaTemplate": "{a} plus {b} : {answer}",
    "flashCards.productModePrompt": "Contre qui jouez-vous ?",
    "flashCards.productVsComputer": "Jouer contre l'ordinateur",
    "flashCards.productTwoPlayers": "Deux joueurs, un écran",
    "flashCards.productHowTo": "Déplacez un trombone à chaque tour, puis tapez le produit des deux trombones pour prendre cette case. Alignez-en quatre pour gagner.",
    "flashCards.productYourTurn": "À vous de jouer",
    "flashCards.productComputerThinking": "L'ordinateur réfléchit…",
    "flashCards.productPlayerTurnTemplate": "Au tour du joueur {n}",
    "flashCards.productPlaceFirstClip": "Placez le trombone A sur n'importe quel facteur. Aucune case n'est prise au premier coup.",
    "flashCards.productPlaceSecondClip": "Placez le trombone B sur un facteur, puis tapez le produit.",
    "flashCards.productMoveOneClip": "Déplacez le trombone A ou le trombone B vers un autre facteur.",
    "flashCards.productTypeProductTemplate": "Combien fait {front} ? Tapez la réponse pour prendre la case.",
    "flashCards.productClipLabelTemplate": "Trombone {name}",
    "flashCards.productMoveClipAriaTemplate": "Déplacer le trombone {name} sur {n}",
    "flashCards.productComputerMovedTemplate": "L'ordinateur a déplacé le trombone {name} sur {n} et a pris {product}.",
    "flashCards.productYouWin": "Quatre alignés : vous avez gagné !",
    "flashCards.productComputerWins": "L'ordinateur a aligné quatre cases.",
    "flashCards.productPlayerWinsTemplate": "Le joueur {n} gagne avec quatre alignés !",
    "flashCards.productDraw": "Plus aucun coup possible : match nul.",
    "flashCards.productChangeMode": "Changer d’adversaire",
    "flashCards.productBoardCaption": "Plateau du jeu des produits",
    "flashCards.productCellFreeTemplate": "{product} : libre",
    "flashCards.productCellYoursTemplate": "{product} : à vous",
    "flashCards.productCellComputerTemplate": "{product} : à l'ordinateur",
    "flashCards.productCellPlayerTemplate": "{product} : au joueur {n}",
    "flashCards.productTurnPasses": "Le tour passe.",
    "flashCards.productVariantPrompt": "À quel jeu voulez-vous jouer ?",
    "flashCards.productVariantProduct": "Jeu des produits (×)",
    "flashCards.productVariantSum": "Jeu des sommes (+)",
    "flashCards.productSumHowTo": "Déplacez un trombone à chaque tour, puis tapez la somme des deux trombones pour prendre une case portant cette somme. S'il y en a plusieurs de libres, c'est vous qui choisissez. Alignez-en quatre pour gagner.",
    "flashCards.productSumPlaceFirstClip": "Placez le trombone A sur n'importe quel nombre. Aucune case n'est prise au premier coup.",
    "flashCards.productSumPlaceSecondClip": "Placez le trombone B sur un nombre, puis tapez la somme.",
    "flashCards.productSumMoveOneClip": "Déplacez le trombone A ou le trombone B vers un autre nombre.",
    "flashCards.productSumTypeTemplate": "Combien fait {front} ? Tapez la réponse pour prendre une case.",
    "flashCards.productChooseSquare": "Choisissez la case à prendre",
    "flashCards.productSumComputerMovedTemplate": "L'ordinateur a déplacé le trombone {name} sur {n} et a pris une case {product}.",
    "flashCards.productSumBoardCaption": "Plateau du jeu des sommes",
    "flashCards.productCellChooseTemplate": "{product} : prendre cette case",
    "flashCards.munchModePrompt": "Comment voulez-vous jouer ?",
    "flashCards.munchWithMonsters": "Avec des monstres",
    "flashCards.munchCalm": "Calme : sans monstres",
    "flashCards.munchHowTo": "Déplacez-vous avec les flèches, ou touchez une case voisine. Appuyez sur Espace, ou touchez-vous, pour croquer. Mangez toutes les cases qui respectent la règle ; une mauvaise case coûte une vie.",
    "flashCards.munchRuleMultiples": "Multiples de {n}",
    "flashCards.munchRuleProducts": "Égal à {n}",
    "flashCards.munchRuleFactors": "Diviseurs de {n}",
    "flashCards.munchLeftTemplate": "Encore {n} à manger",
    "flashCards.munchLevelDone": "Niveau terminé !",
    "flashCards.munchWhyMultipleTemplate": "{v} n'est pas un multiple de {n} : {n} × {k} = {lo} et {n} × {k1} = {hi}.",
    "flashCards.munchWhyMultipleSmallTemplate": "{v} n'est pas un multiple de {n} : le plus petit multiple de {n} est {n}.",
    "flashCards.munchWhyFactorTemplate": "{v} n'est pas un diviseur de {n} : {n} ÷ {v} = {q} reste {r}.",
    "flashCards.munchWhyProductTemplate": "{front} = {p}, pas {n}.",
    "flashCards.munchLostLife": "Vous perdez une vie.",
    "flashCards.munchCaught": "Un monstre vous a attrapé ! Vous perdez une vie.",
    "flashCards.munchYumTemplate": "Miam ! {text} respecte la règle.",
    "flashCards.munchBoardLabel": "Plateau de jeu. Utilisez les flèches pour vous déplacer et Espace pour croquer.",
    "flashCards.munchSquareEaten": "mangée",
    "flashCards.munchSquareYou": "vous êtes ici",
    "flashCards.munchSquareMonster": "un monstre est ici",
    "flashCards.munchUp": "Haut",
    "flashCards.munchDown": "Bas",
    "flashCards.munchLeft": "Gauche",
    "flashCards.munchRight": "Droite",
    "flashCards.munchMunchButton": "Croquer",
    "flashCards.munchScoreCaption": "cases mangées",
    "flashCards.munchReachedTemplate": "Niveau {level} atteint · {wrong} erreurs",
    "flashCards.munchChangeMode": "Changer de façon de jouer",
    "flashCards.munchWhichFacts": "Quels calculs voulez-vous travailler ?",
    "flashCards.munchWhyAddTemplate": "{front} fait {p}, pas {n}.",
    "flashCards.munchWhySubtractTemplate": "{front} donne {p}, pas {n}.",
    "flashCards.munchWhyDivideTemplate": "{front} donne {p}, pas {n}.",
    "flashCards.collieHowTo": "Aidez la border collie à traverser le sol. Seules les dalles qui respectent la règle la supportent ; les autres cassent et elle retombe au départ. Déplacez-la avec les flèches, ou touchez une dalle à côté d'elle.",
    "flashCards.collieStart": "Commencer la traversée",
    "flashCards.collieFloorTemplate": "Sol {n}",
    "flashCards.collieFallsTemplate": "Chutes : {n}",
    "flashCards.collieFellTemplate": "Crac ! {why} Retour au départ.",
    "flashCards.collieSafeTemplate": "{text} tient bon.",
    "flashCards.collieCrossed": "Elle a traversé !",
    "flashCards.collieCrossedClean": "Sans une seule chute.",
    "flashCards.collieCrossedOneFall": "Avec une chute.",
    "flashCards.collieCrossedFallsTemplate": "Avec {n} chutes.",
    "flashCards.collieNextFloor": "Sol suivant",
    "flashCards.collieOtherSide": "L'autre côté",
    "flashCards.collieStartLedge": "Départ",
    "flashCards.collieBoardLabel": "Sol de dalles. Utilisez les flèches pour faire traverser la chienne.",
    "flashCards.collieTileBroken": "cassée",
    "flashCards.collieTileSafe": "sûre",
    "flashCards.collieDogHere": "la chienne est ici",
    "flashCards.collieStarsAriaTemplate": "{n} étoiles sur 3",
    "flashCards.collieWhichFacts": "Quels calculs voulez-vous travailler ?",
    // Math Facts Soccer (ADR 0059).
    "flashCards.soccerHowToPlay": "Chaque penalty est un calcul. Tapez la réponse et appuyez sur Entrée pour tirer. Faites vite : une fois le gardien en place, une bonne réponse est arrêtée.",
    "flashCards.soccerWhichFacts": "Quels calculs ?",
    "flashCards.soccerChooseKeeper": "Choisissez votre gardien pour commencer",
    "flashCards.soccerKeeperRookie": "Débutant",
    "flashCards.soccerKeeperPro": "Pro",
    "flashCards.soccerKeeperLegend": "Légende",
    "flashCards.soccerKeeperSecondsTemplate": "{n} secondes",
    "flashCards.soccerGoalsTemplate": "Buts {n}",
    "flashCards.soccerKickTemplate": "Tir {n} sur {total}",
    "flashCards.soccerRecordAriaTemplate": "{goals} buts sur {kicks} tirs",
    "flashCards.soccerKeeperGettingReady": "Le gardien se place",
    "flashCards.soccerKeeperSetShort": "Gardien en place !",
    "flashCards.soccerKeeperSet": "Le gardien est en place. Une bonne réponse sera maintenant arrêtée.",
    "flashCards.soccerAnswerAria": "Tapez la réponse pour tirer le penalty",
    "flashCards.soccerShoot": "Tirer",
    "flashCards.soccerHint": "Tapez la réponse et appuyez sur Entrée pour tirer. Entrée sur une case vide pour passer.",
    "flashCards.soccerBannerGoal": "BUT !",
    "flashCards.soccerBannerHatTrick": "COUP DU CHAPEAU !",
    "flashCards.soccerBannerSaved": "ARRÊTÉ !",
    "flashCards.soccerBannerWide": "À CÔTÉ !",
    "flashCards.soccerBannerSkipped": "Passé",
    "flashCards.soccerVerdictGoal": "But !",
    "flashCards.soccerVerdictSaved": "Juste, mais le gardien était en place",
    "flashCards.soccerVerdictWide": "À côté",
    "flashCards.soccerVerdictSkipped": "Passé",
    "flashCards.soccerFullTime": "Fin du match",
    "flashCards.soccerGoalsFromTemplate": "buts sur {total} penaltys",
    "flashCards.soccerCountsTemplate": "{saved} arrêtés · {wide} à côté · {skipped} passés",
    "flashCards.soccerSlowHeading": "Vous les saviez. Maintenant, plus vite",
    "flashCards.soccerChangeKeeper": "Choisir un autre gardien",
    "flashCards.footballHowToPlay": "Chaque jeu est un calcul. Quatre receveurs courent leur trajet, chacun avec un numéro sur son maillot. Touchez celui qui porte la réponse, ou appuyez sur 1 à 4, pour lancer. Lancez avant l'arrivée de la pression ou c'est un sac. Une partie compte quatre séries.",
    "flashCards.footballWhichFacts": "Quels calculs ?",
    "flashCards.footballChooseDefense": "Choisissez une défense pour commencer",
    "flashCards.footballDefenseRookie": "Débutante",
    "flashCards.footballDefensePro": "Pro",
    "flashCards.footballDefenseAllPro": "Élite",
    "flashCards.footballDefenseSecondsTemplate": "{n} secondes",
    "flashCards.footballPointsTemplate": "Points {n}",
    "flashCards.footballDriveTemplate": "Série {n} sur {total}",
    "flashCards.footballDownAndDistanceTemplate": "{down} et {distance}",
    "flashCards.footballDownFirst": "1re",
    "flashCards.footballDownSecond": "2e",
    "flashCards.footballDownThird": "3e",
    "flashCards.footballDownFourth": "4e",
    "flashCards.footballGoal": "but",
    "flashCards.footballBallOnTemplate": "Ballon : {spot}",
    "flashCards.footballOwnTemplate": "notre camp, {n}",
    "flashCards.footballOppTemplate": "camp adverse, {n}",
    "flashCards.footballFieldAria": "Le terrain. Quatre receveurs courent vers l'avant. Lancez à celui qui porte la réponse.",
    "flashCards.footballReceiverAriaTemplate": "Receveur {n} : {value}",
    "flashCards.footballReceiverHiddenAriaTemplate": "Receveur {n}",
    "flashCards.footballQuarterback": "QB",
    "flashCards.footballEndZone": "ZONE DE BUT",
    "flashCards.footballRush": "Pression",
    "flashCards.footballNextPlay": "Jeu suivant",
    "flashCards.footballHint": "Touchez un receveur ou appuyez sur 1 à 4 pour lancer. Lancez avant l'arrivée de la pression.",
    "flashCards.footballCaughtTemplate": "Attrapé pour {n} verges.",
    "flashCards.footballCaughtOne": "Attrapé pour 1 verge.",
    "flashCards.footballFirstDown": "Premier jeu.",
    "flashCards.footballTouchdown": "Touché !",
    "flashCards.footballIncompleteTemplate": "Passe incomplète. {front} = {answer}.",
    "flashCards.footballSackedTemplate": "Sac avec une perte de {n} verges. {front} = {answer}.",
    "flashCards.footballSackedOneTemplate": "Sac avec une perte de 1 verge. {front} = {answer}.",
    "flashCards.footballSackedNoLossTemplate": "Sac. {front} = {answer}.",
    "flashCards.footballTurnover": "Balle perdue faute de jeux.",
    "flashCards.footballFinal": "Fin du match",
    "flashCards.footballPointsCaption": "points",
    "flashCards.footballCountsTemplate": "Touchés {touchdowns} · Passes attrapées {completions} sur {attempts} · Sacs {sacks}",
    "flashCards.footballYouThrewTemplate": "vous avez lancé au {n}",
    "flashCards.footballSackedLower": "sac",
    "flashCards.footballChangeDefense": "Changer de défense ou de calculs",
    // Math Runner (ADR 0124).
    "flashCards.runnerHowToPlay": "Chaque rangée de portes est un calcul. Trois voies viennent vers vous et chaque porte porte un numéro. Un seul est la réponse. Allez dans sa voie avec les flèches, les touches 1 à 3, un appui sur l'écran ou un balayage, et choisissez-la avant que la rangée n'arrive au coureur. Vous avez trois cœurs. Le coureur accélère quand vous passez des portes et ralentit quand vous trébuchez.",
    "flashCards.runnerWhichFacts": "Quels calculs ?",
    "flashCards.runnerChoosePace": "Choisissez une allure pour commencer",
    "flashCards.runnerPaceJog": "Footing",
    "flashCards.runnerPaceRun": "Course",
    "flashCards.runnerPaceSprint": "Sprint",
    "flashCards.runnerPaceSecondsTemplate": "{n} secondes au départ",
    "flashCards.runnerScoreTemplate": "Points {n}",
    "flashCards.runnerHeartsAriaTemplate": "Cœurs restants : {n}",
    "flashCards.runnerTrackAria": "La piste. Trois voies de portes viennent vers le coureur. Choisissez la voie qui porte la réponse.",
    "flashCards.runnerLaneAriaTemplate": "Voie {n} : {value}",
    "flashCards.runnerLaneHiddenAriaTemplate": "Voie {n}",
    "flashCards.runnerAnnounceTemplate": "{front}. Voie 1 : {a}. Voie 2 : {b}. Voie 3 : {c}.",
    "flashCards.runnerHint": "Touchez ou glissez vers une voie, ou utilisez les flèches, 1 à 3 ou Espace. Choisissez une voie à chaque rangée : une rangée sans choix est un faux pas.",
    "flashCards.runnerPassed": "Porte franchie !",
    "flashCards.runnerStumbledTemplate": "Faux pas. {front} = {answer}.",
    "flashCards.runnerNoPickTemplate": "Faux pas : aucune voie choisie. {front} = {answer}.",
    "flashCards.runnerFinal": "Course terminée",
    "flashCards.runnerScoreCaption": "portes franchies",
    "flashCards.runnerCountsTemplate": "Mauvaises portes {wrong} · Aucune voie choisie {late}",
    "flashCards.runnerYouPickedTemplate": "vous avez choisi {n}",
    "flashCards.runnerNoPickLower": "aucune voie choisie",
    "flashCards.runnerChangePace": "Changer d'allure ou de calculs",
    // Factor Merge (ADR 0128).
    "flashCards.mergeHowToPlay": "Chaque carte est un objectif. Touchez une tuile, puis une autre pour les multiplier : la deuxième tuile devient le produit. Une fusion n'est permise que si le produit est un facteur d'un des objectifs. Quand une tuile est égale à un objectif, il est atteint et un nouveau est tiré. De nouvelles tuiles arrivent sans cesse, et la partie se termine quand plus aucune fusion n'est possible. Trois fois par partie, vous pouvez retirer une tuile.",
    "flashCards.mergeLevelsLabel": "Niveau",
    "flashCards.mergeLevelG3": "Classe 3",
    "flashCards.mergeLevelG4": "Classe 4",
    "flashCards.mergeLevelG5": "Classe 5",
    "flashCards.mergeLevelG6": "Classe 6",
    "flashCards.mergeLevelG7": "Classe 7",
    "flashCards.mergeLevelG8": "Classe 8",
    "flashCards.mergeBoardAria": "Le plateau. Touchez une tuile, puis une autre pour les fusionner. Les flèches passent d'une tuile à l'autre, et Espace ou Entrée en choisit une.",
    "flashCards.mergeTargetsLabel": "Objectifs",
    "flashCards.mergeTargetMake": "Formez {n}",
    "flashCards.mergeTargetLcm": "PPCM de {a} et {b}",
    "flashCards.mergeTargetLcm3": "PPCM de {a}, {b} et {c}",
    "flashCards.mergeSpokenPower": "{base} puissance {exponent}",
    "flashCards.mergeSpokenSquared": "{base} au carré",
    "flashCards.mergeSpokenCubed": "{base} au cube",
    "flashCards.mergeSpokenTimes": " fois ",
    "flashCards.mergeSpokenEquals": "{powers} égale {value}",
    "flashCards.mergeTargetGcf": "PGCD de {a} et {b}",
    "flashCards.mergeStatusTemplate": "Objectifs atteints : {score} · Fusions refusées : {refused}",
    "flashCards.mergeSelectedTemplate": "Tuile {n} choisie. Touchez une autre tuile pour les fusionner, ou touchez {n} de nouveau pour la reposer.",
    "flashCards.mergeMergedTemplate": "{a} × {b} = {product}.",
    "flashCards.mergeRefusedTemplate": "{a} × {b} = {product}, et {product} n'est pas un facteur de {list}.",
    "flashCards.mergeRefusedAnyTemplate": "{a} × {b} = {product}, qui n'est pas un facteur d'un objectif.",
    "flashCards.mergeCalmerPrefix": "Vous préférez réviser les calculs un par un ? Essayez le ",
    "flashCards.mergeCalmerLink": "quiz de multiplication",
    "flashCards.mergeCalmerSuffix": ".",
    "flashCards.mergeListTwo": "{a} ou {b}",
    "flashCards.mergeListThree": "{a}, {b} ou {c}",
    "flashCards.mergeMadeTemplate": "Objectif atteint : {line}.",
    "flashCards.mergeMadeLcmTemplate": "Objectif atteint : le PPCM de {a} et {b} est {value}. {line}.",
    "flashCards.mergeMadeLcm3Template": "Objectif atteint : le PPCM de {a}, {b} et {c} est {value}. {line}.",
    "flashCards.mergeMadeGcfTemplate": "Objectif atteint : le PGCD de {a} et {b} est {value}. {line}.",
    "flashCards.mergeClearedTemplate": "Tuile {n} retirée.",
    "flashCards.mergeClearOn": "Mode retrait : touchez une tuile pour la retirer. Appuyez de nouveau sur le bouton, ou sur Échap, pour annuler.",
    "flashCards.mergeClearOff": "Retrait annulé.",
    "flashCards.mergeClearButtonTemplate": "Retirer une tuile ({n} restantes)",
    "flashCards.mergeOver": "Plus aucune fusion n'est possible. La partie est terminée.",
    "flashCards.mergeFinal": "Partie terminée",
    "flashCards.mergeScoreCaption": "objectifs atteints",
    "flashCards.mergeSummaryRefusedTemplate": "Fusions refusées : {n}",
    "flashCards.mergePracticeHeading": "Pratiquez hors du jeu",
    // Shop Rush (ADR 0131).
    "flashCards.shopHowToPlay": "Dix clients se présentent au comptoir, un à la fois. Préparez chaque commande, puis appuyez sur Servir. Réussir du premier coup vaut 2 étoiles, réussir au deuxième essai en vaut 1, et un client qui reçoit deux mauvaises réponses s'en va. Un service rapporte au plus 20 étoiles.",
    "flashCards.shopLevelsLabel": "Niveau",
    "flashCards.shopLevelG2": "Classe 2",
    "flashCards.shopLevelG3": "Classe 3",
    "flashCards.shopLevelG4": "Classe 4",
    "flashCards.shopLevelG5": "Classe 5",
    "flashCards.shopLevelG6": "Classe 6",
    "flashCards.shopLevelG7": "Classe 7",
    "flashCards.shopLevelG8": "Classe 8",
    "flashCards.shopLevelNoteG2": "Compter des pièces, rendre la monnaie de 1 $, additionner des prix et fractions de pizza.",
    "flashCards.shopLevelNoteG3": "Monnaie de 1 $, 2 $ et 5 $, additions de prix, tables et fractions de pizza.",
    "flashCards.shopLevelNoteG4": "Monnaie jusqu'à 10 $, notes plus longues, un chiffre par deux et fractions de pizza.",
    "flashCards.shopLevelNoteG5": "Monnaie jusqu'à 20 $, notes au centime, deux chiffres par deux et fractions de pizza.",
    "flashCards.shopLevelNoteG6": "Réductions et pourboires à pourcentages simples, prix unitaire et meilleur achat.",
    "flashCards.shopLevelNoteG7": "Réductions, taxe de vente, pourboires et majoration.",
    "flashCards.shopLevelNoteG8": "Réduction puis taxe, prix initial, variation en pourcentage et intérêt simple.",
    "flashCards.shopPacesLabel": "Rythme",
    "flashCards.shopPaceCalm": "Calme",
    "flashCards.shopPaceBusy": "Affluence",
    "flashCards.shopPaceRush": "Coup de feu",
    "flashCards.shopPaceNoClock": "Sans chrono",
    "flashCards.shopPaceSecondsTemplate": "{n} secondes par client",
    "flashCards.shopStart": "Commencer le service",
    "flashCards.shopCalmerPrefix": "Vous préférez vous entraîner aux additions une par une ? Essayez le ",
    "flashCards.shopCalmerLink": "quiz d'addition",
    "flashCards.shopCalmerSuffix": ".",
    "flashCards.shopStatusTemplate": "Client {n} sur {total} · Étoiles : {stars} sur {max}",
    "flashCards.shopCounterLabel": "Le comptoir",
    "flashCards.shopPatienceLabel": "Patience",
    "flashCards.shopPause": "Pause",
    "flashCards.shopResume": "Reprendre",
    "flashCards.shopPausedNote": "En pause. Appuyez sur Reprendre pour continuer.",
    "flashCards.shopServe": "Servir",
    "flashCards.shopNextCustomer": "Client suivant",
    "flashCards.shopFinish": "Terminer le service",
    "flashCards.shopUndo": "Annuler",
    "flashCards.shopClear": "Tout effacer",
    "flashCards.shopBackspace": "Effacer un chiffre",
    "flashCards.shopGivenTemplate": "Rendu : {amount}",
    "flashCards.shopAddTemplate": "Ajouter {amount}",
    "flashCards.shopTrayLabel": "Pièces et billets",
    "flashCards.shopKeypadLabel": "Clavier de la caisse",
    "flashCards.shopEntryLabel": "Votre réponse",
    "flashCards.shopOffersLabel": "Les deux offres",
    "flashCards.shopOfferTemplate": "{count} pour {price}",
    "flashCards.shopBoardLabel": "Tableau des prix",
    "flashCards.shopCoinsLabel": "Les pièces",
    "flashCards.shopArriveTemplate": "Client {n}. {order}",
    "flashCards.shopOrderChange": "Paie {paid} pour un en-cas à {price}. Rendez la monnaie.",
    "flashCards.shopOrderTotal": "Encaissez {lines}. Quel est le total ?",
    "flashCards.shopOrderGroups": "{groups} {container} de {each} {thing} : combien y en a-t-il en tout ?",
    "flashCards.shopOrderFraction": "{fraction} d'une pizza coupée en {slices} parts : combien de parts ?",
    "flashCards.shopOrderDiscountSale": "{rate} % de réduction sur {price} : quel est le prix soldé ?",
    "flashCards.shopOrderDiscountOff": "{rate} % de réduction sur {price} : quel est le montant de la réduction ?",
    "flashCards.shopOrderTax": "{price} plus {rate} % de taxe : quel est le total ?",
    "flashCards.shopOrderTip": "Un pourboire de {rate} % sur une addition de {price} : quel est le pourboire ?",
    "flashCards.shopOrderBetterBuy": "{a}, ou {b} : lequel coûte le moins cher l'unité ?",
    "flashCards.shopOrderCoins": "Le client paie avec ces pièces. Combien d'argent cela fait-il ?",
    "flashCards.shopOrderUnitPrice": "{count} {items} pour {total} : combien coûte un seul ?",
    "flashCards.shopOrderMarkup": "La boutique l'a payé {cost} et le majore de {rate} %. Quel est le prix de vente ?",
    "flashCards.shopOrderDiscountTax": "{price}, {discount} % de réduction, puis {tax} % de taxe : quel est le total ?",
    "flashCards.shopOrderOriginalPrice": "Après {rate} % de réduction, il coûte {sale}. Quel était le prix initial ?",
    "flashCards.shopOrderPercentUp": "Le prix est passé de {from} à {to}. De quel pourcentage a-t-il augmenté ?",
    "flashCards.shopOrderPercentDown": "Le prix est passé de {from} à {to}. De quel pourcentage a-t-il baissé ?",
    "flashCards.shopOrderInterest": "{principal} à {rate} % par an pendant {years} ans : quel est l'intérêt ?",
    "flashCards.shopOrderInterestOne": "{principal} à {rate} % par an pendant 1 an : quel est l'intérêt ?",
    "flashCards.shopServedFirst": "Merci ! Deux étoiles.",
    "flashCards.shopServedSecond": "Merci ! Une étoile.",
    "flashCards.shopMissTemplate": "Pas tout à fait. {hint} Réessayez.",
    "flashCards.shopLeftWrongTemplate": "Le client est parti. {worked}",
    "flashCards.shopLeftPatienceTemplate": "Plus de patience : le client est parti. {worked}",
    "flashCards.shopItemApple": "pomme",
    "flashCards.shopItemApplePlural": "pommes",
    "flashCards.shopItemBanana": "banane",
    "flashCards.shopItemBananaPlural": "bananes",
    "flashCards.shopItemCookie": "biscuit",
    "flashCards.shopItemCookiePlural": "biscuits",
    "flashCards.shopItemJuice": "boîte de jus",
    "flashCards.shopItemJuicePlural": "boîtes de jus",
    "flashCards.shopItemMilk": "brique de lait",
    "flashCards.shopItemMilkPlural": "briques de lait",
    "flashCards.shopItemBread": "pain",
    "flashCards.shopItemBreadPlural": "pains",
    "flashCards.shopItemSandwich": "sandwich",
    "flashCards.shopItemSandwichPlural": "sandwichs",
    "flashCards.shopItemNotebook": "cahier",
    "flashCards.shopItemNotebookPlural": "cahiers",
    "flashCards.shopItemSticker": "autocollant",
    "flashCards.shopItemStickerPlural": "autocollants",
    "flashCards.shopItemPencil": "crayon",
    "flashCards.shopItemPencilPlural": "crayons",
    "flashCards.shopContainerBags": "sacs",
    "flashCards.shopContainerBoxes": "boîtes",
    "flashCards.shopContainerTrays": "plateaux",
    "flashCards.shopContainerBaskets": "paniers",
    "flashCards.shopContainerPacks": "paquets",
    "flashCards.shopContainerJars": "bocaux",
    "flashCards.shopContainerCrates": "caisses",
    "flashCards.shopContainerCartons": "cartons",
    "flashCards.shopThingRolls": "petits pains",
    "flashCards.shopThingCrayons": "crayons de couleur",
    "flashCards.shopThingMuffins": "muffins",
    "flashCards.shopThingApples": "pommes",
    "flashCards.shopThingStickers": "autocollants",
    "flashCards.shopThingCookies": "biscuits",
    "flashCards.shopThingOranges": "oranges",
    "flashCards.shopThingEggs": "œufs",
    "flashCards.shopHintChange": "Comptez en avançant de {price} jusqu'à {paid}.",
    "flashCards.shopHintTotal": "Additionnez les dollars, puis les cents.",
    "flashCards.shopHintGroups": "{groups} groupes de {each}.",
    "flashCards.shopHintFraction": "Partagez {slices} parts en {den} parties égales : {slices} ÷ {den} = {per}.",
    "flashCards.shopHintFractionSplit": "Partagez {slices} parts en {den} parties égales.",
    "flashCards.shopHintPercentOf": "Calculez d'abord {rate} % de {price}.",
    "flashCards.shopHintTenthBuild": "10 % de {price}, c'est {tenth}. Construisez {rate} % à partir de là.",
    "flashCards.shopHintTenthDivide": "10 %, c'est un dixième : divisez {price} par 10.",
    "flashCards.shopHintTenth": "10 % de {price}, c'est {tenth}.",
    "flashCards.shopHintQuarter": "25 %, c'est un quart : divisez {price} par 4.",
    "flashCards.shopHintTip15": "10 % de {price}, c'est {tenth}, et 5 % en est la moitié.",
    "flashCards.shopHintTip18": "10 % de {price}, c'est {tenth}. Calculez 20 %, puis retirez 2 %.",
    "flashCards.shopHintBetterBuy": "Trouvez le prix d'une unité dans chaque offre.",
    "flashCards.shopHintCoins": "Comptez d'abord les pièces de 25¢, puis celles de 10¢, de 5¢ et de 1¢.",
    "flashCards.shopHintTotalCents": "Additionnez les prix un par un.",
    "flashCards.shopHintFractionSlice": "Chaque part est 1/{den} de la pizza. Comptez combien de parts font {fraction}.",
    "flashCards.shopHintUnitPrice": "Partagez {total} en parts égales entre {count}.",
    "flashCards.shopHintDiscountTax": "Retirez d'abord la réduction, puis ajoutez la taxe sur le prix soldé.",
    "flashCards.shopHintOriginalPrice": "Après {rate} % de réduction, {sale} représente {rest} % du prix.",
    "flashCards.shopHintPercentChange": "Trouvez la variation, puis divisez-la par le prix de départ.",
    "flashCards.shopHintInterest": "L'intérêt d'une année est {rate} % de {principal}.",
    "flashCards.shopWorkedChange": "{steps}, donc la monnaie est {change}.",
    "flashCards.shopWorkedTotal": "{terms} = {total}.",
    "flashCards.shopWorkedGroups": "{groups} × {each} = {answer}.",
    "flashCards.shopWorkedFraction": "{fraction} de {slices} : {slices} ÷ {den} = {per}, puis {per} × {num} = {answer}.",
    "flashCards.shopWorkedDiscountSale": "{rate} % de {price}, c'est {off} ; {price} − {off} = {sale}.",
    "flashCards.shopWorkedDiscountOff": "{rate} % de {price}, c'est {off}.",
    "flashCards.shopWorkedTax": "{rate} % de {price}, c'est {tax} ; {price} + {tax} = {total}.",
    "flashCards.shopWorkedTip10": "10 % de {bill} = {bill} ÷ 10 = {tip}.",
    "flashCards.shopWorkedTip15": "10 % = {tenth} et 5 % = {half}, donc 15 % = {tenth} + {half} = {tip}.",
    "flashCards.shopWorkedTip18": "20 % = {twenty} et 2 % = {two}, donc 18 % = {twenty} − {two} = {tip}.",
    "flashCards.shopWorkedTip20": "10 % = {tenth}, donc 20 % = {tenth} × 2 = {tip}.",
    "flashCards.shopWorkedTip25": "25 % de {bill} = {bill} ÷ 4 = {tip}.",
    "flashCards.shopWorkedBetterBuy": "{priceA} ÷ {countA} = {unitA}, et {priceB} ÷ {countB} = {unitB}, donc {winnerCount} pour {winnerPrice}.",
    "flashCards.shopWorkedCoins": "{terms} = {total}.",
    "flashCards.shopWorkedUnitPrice": "{total} ÷ {count} = {unit}.",
    "flashCards.shopWorkedDiscountTax": "{discount} % de {price}, c'est {off}, donc le prix soldé est {sale} ; {tax} % de {sale}, c'est {taxAmount} ; {sale} + {taxAmount} = {total}.",
    "flashCards.shopWorkedOriginalPrice": "{rest} %, c'est {sale}, donc 10 %, c'est {tenth} et 100 %, c'est {original}.",
    "flashCards.shopWorkedPercentChange": "{big} − {small} = {diff} ; {diff} ÷ {from} = {ratio}, donc {percent} %.",
    "flashCards.shopWorkedInterest": "{rate} % de {principal}, c'est {yearly} par an ; {yearly} × {years} = {interest}.",
    "flashCards.shopWorkedInterestOne": "{rate} % de {principal}, c'est {interest}.",
    "flashCards.shopFinal": "Service terminé",
    "flashCards.shopScoreCaptionTemplate": "étoiles sur {max}",
    "flashCards.shopSummaryServedTemplate": "Commandes servies : {n} sur {total}",
    "flashCards.shopSummaryWrongTemplate": "Essais ratés : {n}",
    "flashCards.shopSummaryWalkedTemplate": "Clients à bout de patience : {n}",
    "flashCards.shopPracticeHeading": "Pratiquez hors du jeu",
    // Hidden Picture (ADR 0133).
    "flashCards.pictureHowToPlay": "Une image se cache sous 25 cases, et chaque case est un calcul. Touchez une case, tapez la réponse et appuyez sur Vérifier. Une bonne réponse soulève la case. Une erreur donne droit à un deuxième essai, et après une deuxième erreur la réponse s'affiche et la case se soulève quand même.",
    "flashCards.pictureWhichFacts": "Quels calculs ?",
    "flashCards.pictureStart": "Commencer",
    "flashCards.pictureHintSnowman": "Quelque chose pour l'hiver se cache sous les cases.",
    "flashCards.pictureHintHeart": "Quelque chose pour la Saint-Valentin se cache sous les cases.",
    "flashCards.pictureHintShamrock": "Quelque chose pour la Saint-Patrick se cache sous les cases.",
    "flashCards.pictureHintTulip": "Quelque chose pour le printemps se cache sous les cases.",
    "flashCards.pictureHintIcecream": "Quelque chose pour l'été se cache sous les cases.",
    "flashCards.pictureHintApple": "Quelque chose pour l'automne et la rentrée se cache sous les cases.",
    "flashCards.pictureHintPumpkin": "Quelque chose pour Halloween se cache sous les cases.",
    "flashCards.pictureHintTurkey": "Quelque chose pour Thanksgiving se cache sous les cases.",
    "flashCards.pictureHintGift": "Quelque chose pour les fêtes d'hiver se cache sous les cases.",
    "flashCards.pictureCaptionSnowman": "Un bonhomme de neige pour l'hiver !",
    "flashCards.pictureCaptionHeart": "Un cœur pour la Saint-Valentin !",
    "flashCards.pictureCaptionShamrock": "Un trèfle pour la Saint-Patrick !",
    "flashCards.pictureCaptionTulip": "Une tulipe pour le printemps !",
    "flashCards.pictureCaptionIcecream": "Un cornet de glace pour l'été !",
    "flashCards.pictureCaptionApple": "Une pomme pour la rentrée !",
    "flashCards.pictureCaptionPumpkin": "Une citrouille avec un visage pour Halloween !",
    "flashCards.pictureCaptionTurkey": "Une dinde pour Thanksgiving !",
    "flashCards.pictureCaptionGift": "Un cadeau emballé pour les fêtes d'hiver !",
    "flashCards.pictureLeftTemplate": "Cases restantes : {n}",
    "flashCards.pictureRightTemplate": "Réussies du premier coup : {n}",
    "flashCards.pictureBoardLabel": "Cases. Choisissez-en une pour répondre.",
    "flashCards.pictureTileAriaTemplate": "Ligne {row}, case {col} : {fact}",
    "flashCards.pictureCheck": "Vérifier",
    "flashCards.pictureRight": "Bravo !",
    "flashCards.pictureTryAgain": "Pas tout à fait. Essayez encore.",
    "flashCards.pictureAnswerShownTemplate": "La réponse est {fact} = {answer}.",
    "flashCards.pictureAnnounceRightTemplate": "Bravo. Cases restantes : {n}.",
    "flashCards.pictureAnnounceWrongTemplate": "Pas tout à fait. Essayez encore : {fact}.",
    "flashCards.pictureAnnounceMissedTemplate": "La réponse est {fact} = {answer}. Cases restantes : {n}.",
    "flashCards.pictureAnnounceNextTemplate": "Suivant : {fact}.",
    "flashCards.pictureAnnounceDone": "L'image est complète.",
    "flashCards.pictureHint": "Touchez une autre case pour répondre à celle-là à la place. Appuyez sur Entrée pour vérifier.",
    "flashCards.pictureFinal": "L'image est complète",
    "flashCards.pictureScoreTemplate": "{n} sur {total} cases réussies du premier coup",
    "flashCards.pictureSummaryWrongTemplate": "Essais ratés : {n}",
    "flashCards.pictureMoreHeading": "Continuez à vous entraîner",
    // Slingshot Plate Smash (ADR 0060).
    "flashCards.platesHowToPlay": "Lisez le calcul sur la pancarte, puis cassez l'assiette qui porte la réponse. Tirez vers l'arrière sur le stand et relâchez pour lancer. Vous avez 60 secondes : 10 points par assiette touchée du premier coup, et le double dès trois de suite.",
    "flashCards.platesWhichFacts": "Quels calculs ?",
    "flashCards.platesChooseMode": "Choisissez un stand pour commencer",
    "flashCards.platesEasy": "Facile",
    "flashCards.platesHard": "Difficile",
    "flashCards.platesEasyHint": "Les assiettes sont immobiles",
    "flashCards.platesHardHint": "Les assiettes défilent sur un tapis",
    "flashCards.platesBoothAria": "Stand du lance-pierre. Tirez vers l'arrière et relâchez pour lancer, ou visez avec les flèches et lancez avec Espace.",
    "flashCards.platesHint": "Tirez vers l'arrière et relâchez pour lancer. Les flèches visent, Espace lance.",
    "flashCards.platesAimingAtTemplate": "Vise le {n}",
    "flashCards.platesAimingAtNothing": "Vise entre les assiettes",
    "flashCards.platesHotStreak": "Série ×2",
    "flashCards.platesVerdictHitTemplate": "Cassée ! +{n}",
    "flashCards.platesVerdictLateTemplate": "La voilà : {n}. Pas de points après une mauvaise assiette.",
    "flashCards.platesVerdictWrongTemplate": "Cette assiette était {n}. Réessayez !",
    "flashCards.platesVerdictMiss": "Aucune assiette touchée",
    "flashCards.platesTimeUp": "Temps écoulé !",
    "flashCards.platesPointsCaption": "points",
    "flashCards.platesCountsTemplate": "{hits} cassées du premier coup · {wrong} mauvaises assiettes",
    "flashCards.platesMadeTheBoard": "Vous entrez dans le classement !",
    "flashCards.platesNameLabel": "Votre nom pour le classement",
    "flashCards.platesNamePlaceholder": "Prénom ou surnom",
    "flashCards.platesSave": "Enregistrer",
    "flashCards.platesSkip": "Ne pas enregistrer ce score",
    "flashCards.platesRankTemplate": "Vous êtes numéro {n} au classement !",
    "flashCards.platesYouHitTemplate": "vous avez cassé {n}",
    "flashCards.platesChangeMode": "Choisir un autre stand",
    "flashCards.platesLeaderboard": "Classement",
    "flashCards.platesBoardEmpty": "Aucun score pour l'instant. Soyez le premier !",
    "flashCards.platesBoardName": "Nom",
    "flashCards.platesBoardScore": "Score",
    "flashCards.platesBoardFacts": "Calculs",
    "flashCards.platesBoardNote": "Enregistré dans ce navigateur uniquement. Rien n'est envoyé.",
    "flashCards.platesClear": "Effacer le classement",
    "flashCards.platesClearConfirm": "Effacer tous les scores et les noms ?",
    "flashCards.platesClearYes": "Effacer",
    "flashCards.platesClearNo": "Garder",
    // The Baker Street Math Crossword (ADR 0134).
    "flashCards.crosswordHowToPlay": "Chaque ligne de la grille est une phrase de nombres, qui se lit de gauche à droite ou de haut en bas, et presque tous les nombres sont dans deux lignes à la fois. Remplissez les cases vides pour que toutes les lignes soient vraies. Résolvez cinq affaires pour clore le dossier. Chaque affaire vaut jusqu'à 3 loupes : Demander à Holmes et une Vérification qui trouve une erreur en coûtent une chacune, et une affaire résolue rapporte toujours au moins une loupe.",
    "flashCards.crosswordRanksLabel": "Grade",
    "flashCards.crosswordRankConstable": "Agent",
    "flashCards.crosswordRankInspector": "Inspecteur",
    "flashCards.crosswordRankDetective": "Détective",
    "flashCards.crosswordRankGradesConstable": "CP–CE1",
    "flashCards.crosswordRankGradesInspector": "CE2–CM1",
    "flashCards.crosswordRankGradesDetective": "CM2–6e",
    "flashCards.crosswordRankNoteConstable": "Additions et soustractions dans les 20, sur une grille de neuf nombres. Une banque des nombres manquants est posée sur le bureau.",
    "flashCards.crosswordRankNoteInspector": "Additions et soustractions jusqu'à 100, ou multiplications et divisions des tables jusqu'à 10 × 10, sur une grille de neuf nombres. Une banque des nombres manquants est posée sur le bureau.",
    "flashCards.crosswordRankNoteDetective": "Une grille plus grande avec trois nombres par ligne : la multiplication et la division passent avant l'addition et la soustraction. Pas de banque de nombres.",
    "flashCards.crosswordStart": "Ouvrir le dossier",
    "flashCards.crosswordCalmerPrefix": "Vous préférez vous entraîner sur une opération à la fois ? Essayez le ",
    "flashCards.crosswordCalmerLink": "quiz d'addition",
    "flashCards.crosswordCalmerSuffix": ".",
    "flashCards.crosswordPlateStreet": "Baker Street",
    "flashCards.crosswordStatusTemplate": "Affaire {n} sur {total} · Loupes : {glasses} sur {max}",
    "flashCards.crosswordWorthTemplate": "Cette affaire : {n} sur {max}",
    "flashCards.crosswordBoardLabel": "Les mots croisés",
    "flashCards.crosswordLinesLabel": "Les lignes",
    "flashCards.crosswordBankLabel": "Nombres manquants. Choisissez-en un pour le mettre dans la case sélectionnée.",
    "flashCards.crosswordCheck": "Vérifier",
    "flashCards.crosswordAsk": "Demander à Holmes",
    "flashCards.crosswordNextCase": "Affaire suivante",
    "flashCards.crosswordFinish": "Clore le dossier",
    "flashCards.crosswordArriveTemplate": "Affaire {n}. {title}. {brief}",
    "flashCards.crosswordSolvedTemplate": "Élémentaire. Affaire classée avec {n} loupes sur {max}.",
    "flashCards.crosswordCheckWrongOne": "Un nombre n'est pas juste. Il est marqué. Cela coûte une loupe.",
    "flashCards.crosswordCheckWrongMany": "{n} nombres ne sont pas justes. Ils sont marqués. Cela coûte une loupe.",
    "flashCards.crosswordCheckRight": "Tout ce qui est rempli jusqu'ici est juste.",
    "flashCards.crosswordCheckEmpty": "Écrivez d'abord un nombre, puis vérifiez.",
    "flashCards.crosswordHintTemplate": "{line} dit {open}. Seul {value} convient : {closed}.",
    "flashCards.crosswordLineAcross": "Horizontale {n}",
    "flashCards.crosswordLineDown": "Verticale {n}",
    "flashCards.crosswordTagAcross": "H{n}",
    "flashCards.crosswordTagDown": "V{n}",
    "flashCards.crosswordPositionFirst": "premier nombre",
    "flashCards.crosswordPositionSecond": "deuxième nombre",
    "flashCards.crosswordPositionThird": "troisième nombre",
    "flashCards.crosswordPositionAnswer": "résultat",
    "flashCards.crosswordCellLabelTemplate": "{line}, {position}",
    "flashCards.crosswordLineReadingTemplate": "{line} : {sentence}",
    "flashCards.crosswordBlankWord": "case vide",
    "flashCards.crosswordFinal": "Affaire classée",
    "flashCards.crosswordScoreCaptionTemplate": "loupes sur {max}",
    "flashCards.crosswordSummaryUnaidedTemplate": "Affaires résolues sans demander à Holmes : {n} sur {total}",
    "flashCards.crosswordSummaryWrongTemplate": "Nombres faux trouvés par la vérification : {n}",
    "flashCards.crosswordSummaryHolmesTemplate": "Nombres remplis par Holmes : {n}",
    "flashCards.crosswordPracticeHeading": "S'entraîner hors du jeu",
    "flashCards.crosswordCase1Title": "L'affaire du registre barbouillé",
    "flashCards.crosswordCase1Brief": "Les comptes de Mme Hudson ont pris la pluie. Retrouvez les chiffres que l'humidité a emportés.",
    "flashCards.crosswordCase2Title": "L'affaire du télégramme de minuit",
    "flashCards.crosswordCase2Brief": "Un télégramme est arrivé à Baker Street à minuit, mais certains chiffres ont été déchirés.",
    "flashCards.crosswordCase3Title": "L'affaire de l'horloger silencieux",
    "flashCards.crosswordCase3Brief": "La liste de prix de l'horloger a été trouvée sur son établi, avec quelques nombres effacés.",
    "flashCards.crosswordCase4Title": "L'affaire du quai dans la brume",
    "flashCards.crosswordCase4Brief": "La brume a recouvert une liste de cargaison du quai de la Tamise et caché quelques chiffres.",
    "flashCards.crosswordCase5Title": "L'affaire de la caisse fermée à clé",
    "flashCards.crosswordCase5Brief": "L'inspecteur Lestrade a ouvert la caisse, mais il manque quelques nombres sur sa feuille de comptes.",
    "flashCards.crosswordCase6Title": "L'affaire du parapluie oublié",
    "flashCards.crosswordCase6Brief": "Un parapluie est revenu à Baker Street avec une page de chiffres attachée, dont la moitié est tachée.",
    "flashCards.crosswordCase7Title": "L'affaire du réverbère penché",
    "flashCards.crosswordCase7Brief": "L'allumeur de réverbères a noté sa tournée, mais le gaz a fait pâlir quelques nombres.",
    "flashCards.crosswordCase8Title": "L'affaire de la curieuse boîte à thé",
    "flashCards.crosswordCase8Brief": "Mme Hudson a trouvé un calcul dans une vieille boîte à thé, et quelques nombres sont brouillés.",
    "flashCards.crosswordCase9Title": "L'affaire de l'escalier qui murmure",
    "flashCards.crosswordCase9Brief": "Quelqu'un a glissé une page de calculs sous la porte du 221B, et quelques nombres ont disparu.",
    "flashCards.crosswordCase10Title": "L'affaire du cachet pâli",
    "flashCards.crosswordCase10Brief": "Les colis de la poste ont des étiquettes pâlies. Remplissez les chiffres pour que toutes les lignes soient vraies.",
    "flashCards.crosswordCase11Title": "L'affaire du cocher vagabond",
    "flashCards.crosswordCase11Brief": "Un cocher tenait le compte de ses courses, et la pluie en a effacé une partie.",
    "flashCards.crosswordCase12Title": "L'affaire du violon emprunté",
    "flashCards.crosswordCase12Brief": "Le docteur Watson a trouvé une page de chiffres dans l'étui du violon. Aidez Holmes à la lire.",
} satisfies Record<FlashCardsStringKey, string>;

export const flashCardsStrings: Record<Locale, Record<FlashCardsStringKey, string>> = {
    en,
    es,
    fr,
};
